import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import MouseFooter from "../components/MouseFooter";

const sections = [
  { id: "overview",            label: "Overview" },
  { id: "architecture",        label: "Architecture" },
  { id: "desktop",             label: "Ego Desktop App" },
  { id: "dapp-ide",            label: "dApp IDE" },
  { id: "urego",               label: "Urego Smart Contracts" },
  { id: "smart-contract-guide",label: "Building a Contract" },
  { id: "blocks",              label: "Block Specification" },
  { id: "poc",                 label: "Proof of Coverage" },
  { id: "post",                label: "Proof of Spacetime" },
  { id: "porep",               label: "Proof of Replication" },
  { id: "pqc",                 label: "Post-Quantum Crypto" },
  { id: "drs",                 label: "Reward Scoring" },
  { id: "consensus",           label: "Consensus" },
  { id: "networking",          label: "Networking" },
  { id: "tokenomics",          label: "Tokenomics" },
  { id: "transactions",        label: "Transactions" },
  { id: "privacy",             label: "Shielded Transactions" },
  { id: "storage",             label: "Storage Deals" },
  { id: "compute",             label: "DePIN Compute" },
  { id: "nodes",               label: "Node Types & Joining" },
  { id: "ego-devices",         label: "Ego Devices (Coming)" },
  { id: "governance",          label: "Governance" },
  { id: "security",            label: "Security Model" },
  { id: "eips",                label: "Ego Improvement Proposals" },
  { id: "walletconnect",       label: "WalletConnect (EGO-40)" },
  { id: "evm",                 label: "EVM Compatibility (EGO-41)" },
  { id: "bridge",              label: "Cross-Chain Bridge (EGO-42)" },
  { id: "extension",           label: "Browser Extension Wallet" },
  { id: "dex",                 label: "DEX & AMM (EGO-30)" },
  { id: "p2p",                 label: "P2P Trade & Escrow" },
  { id: "mobile",              label: "Mobile Wallet" },
  { id: "presale",             label: "Pre-Sale (Seed Round)" },
];

function useActiveSection() {
  const [active, setActive] = useState("overview");
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 140;
      let current = sections[0].id;
      for (const { id } of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= scrollY) {
          current = id;
        }
      }
      setActive(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return active;
}

function Tag({ children, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px",
      background: color ? `${color}18` : "rgba(0,229,176,0.10)",
      border: `1px solid ${color ? color + "50" : "rgba(0,229,176,0.30)"}`,
      borderRadius: 4, color: color || "#00e5b0", fontSize: 11,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: 1, textTransform: "uppercase", marginRight: 8, marginBottom: 4,
    }}>{children}</span>
  );
}

function SectionHeader({ eyebrow, title, subtitle, color }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ marginBottom: 10 }}><Tag color={color}>{eyebrow}</Tag></div>
      <h2 style={{
        fontSize: "clamp(24px, 3.5vw, 38px)", fontFamily: "'Syne', sans-serif",
        fontWeight: 800, color: "#fff", margin: "0 0 14px 0",
        letterSpacing: -0.5, lineHeight: 1.15,
      }}>{title}</h2>
      {subtitle && (
        <p style={{ color: "#7a8fa8", fontSize: 15, lineHeight: 1.8, maxWidth: 700, margin: 0 }}>
          {subtitle}
        </p>
      )}
      <div style={{ width: 48, height: 2, background: `linear-gradient(90deg, ${color || "#00e5b0"}, transparent)`, marginTop: 20 }} />
    </div>
  );
}

function Prose({ children }) {
  return (
    <p style={{ color: "#7a8fa8", fontSize: 14.5, lineHeight: 1.85, marginBottom: 20 }}>
      {children}
    </p>
  );
}

function SubHeading({ children }) {
  return (
    <h3 style={{
      color: "#c9d4e3", fontFamily: "'Syne', sans-serif", fontWeight: 700,
      fontSize: 16, margin: "32px 0 12px",
    }}>{children}</h3>
  );
}

function InfoCard({ icon, title, children, accent }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 12, padding: "22px 24px", marginBottom: 14,
        transition: "border-color 0.2s, background 0.2s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = accent ? accent + "50" : "rgba(0,229,176,0.25)";
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
        e.currentTarget.style.background = "rgba(255,255,255,0.025)";
      }}
    >
      {icon && <div style={{ fontSize: 20, marginBottom: 10 }}>{icon}</div>}
      {title && <h4 style={{ color: "#e0e8f4", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 8, marginTop: 0 }}>{title}</h4>}
      <div style={{ color: "#7a8fa8", fontSize: 13.5, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function CalloutBox({ type, children }) {
  const styles = {
    info:    { bg: "rgba(0,102,255,0.07)",  border: "rgba(0,102,255,0.25)",  icon: "ℹ️",  label: "Note" },
    warning: { bg: "rgba(245,158,11,0.07)", border: "rgba(245,158,11,0.25)", icon: "⚠️",  label: "Important" },
    success: { bg: "rgba(0,229,176,0.07)",  border: "rgba(0,229,176,0.25)", icon: "✅", label: "Tip" },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 10, padding: "14px 18px", margin: "20px 0",
      display: "flex", gap: 12, alignItems: "flex-start",
    }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
      <div>
        <div style={{ color: "#c9d4e3", fontWeight: 700, fontSize: 12, fontFamily: "'Syne', sans-serif", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
        <div style={{ color: "#8a9bb5", fontSize: 13.5, lineHeight: 1.7 }}>{children}</div>
      </div>
    </div>
  );
}

function FormulaBlock({ children }) {
  return (
    <div style={{
      background: "rgba(0,0,0,0.45)", border: "1px solid rgba(0,229,176,0.15)",
      borderLeft: "3px solid #00e5b0", borderRadius: "0 8px 8px 0",
      padding: "16px 20px", fontFamily: "'JetBrains Mono', monospace",
      fontSize: 12.5, color: "#b8e8d8", margin: "16px 0",
      overflowX: "auto", whiteSpace: "pre", lineHeight: 1.9,
    }}>{children}</div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: "11px 16px", textAlign: "left",
                background: "rgba(0,229,176,0.06)",
                borderBottom: "1px solid rgba(0,229,176,0.15)",
                color: "#00e5b0", fontFamily: "'Syne', sans-serif",
                fontWeight: 700, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "11px 16px",
                  color: j === 0 ? "#c9d4e3" : "#7a8fa8",
                  fontFamily: j === 0 ? "'JetBrains Mono', monospace" : "inherit",
                  fontSize: j === 0 ? 12 : 13,
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WeightBar({ label, value, pct, color }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ color: "#b0bdd0", fontSize: 13 }}>{label}</span>
        <span style={{ color: color || "#00e5b0", fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>{value}</span>
      </div>
      <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: color || "linear-gradient(90deg, #00e5b0, #0066ff)",
          borderRadius: 3, transition: "width 1s ease",
        }} />
      </div>
    </div>
  );
}

function StepCard({ number, title, children }) {
  return (
    <div style={{ display: "flex", gap: 18, marginBottom: 24 }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: "rgba(0,229,176,0.10)", border: "1px solid rgba(0,229,176,0.30)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#00e5b0", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
      }}>{number}</div>
      <div>
        <div style={{ color: "#e0e8f4", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{title}</div>
        <div style={{ color: "#7a8fa8", fontSize: 13.5, lineHeight: 1.75 }}>{children}</div>
      </div>
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "48px 0" }} />;
}

export default function Docs() {
  const active = useActiveSection();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
      setSidebarOpen(false);
    }
  };

  const goToSection = (e, id) => {
    e.preventDefault();
    sessionStorage.setItem("scrollTo", id);
    navigate("/");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#070b11", color: "#e0e8f4" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Navbar */}
      <header className="navbar">
        <div className="logo">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/ego.png" alt="Ego Blockchain Logo" className="logo-img" />
          </Link>
        </div>
        <button className={`hamburger ${menuOpen ? "active" : ""}`} onClick={() => setMenuOpen(!menuOpen)}>
          <span></span><span></span><span></span>
        </button>
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
          <Link to="/download" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Download Ego Desktop</Link>
          <Link to="/blog" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Blog</Link>
          <Link to="/docs" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Docs</Link>
          <Link to="/egoscan" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>EgoScan</Link>
          <Link to="/claim" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Claim EGOC</Link>
        </nav>
      </header>
      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)} />}

      {/* Breadcrumb bar */}
      <div style={{
        position: "sticky", top: 70, zIndex: 40,
        background: "rgba(7,11,17,0.96)", backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
      }}>
        <Link to="/" style={{ color: "#5a6a80", textDecoration: "none", fontSize: 12.5 }}
          onMouseEnter={e => e.currentTarget.style.color = "#c9d4e3"}
          onMouseLeave={e => e.currentTarget.style.color = "#5a6a80"}>
          ← Home
        </Link>
        <span style={{ color: "rgba(255,255,255,0.1)" }}>|</span>
        <span style={{ color: "#00e5b0", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace" }}>docs</span>
        <span style={{ color: "rgba(255,255,255,0.1)" }}>/</span>
        <span style={{ color: "#c9d4e3", fontSize: 12.5 }}>{sections.find(s => s.id === active)?.label || active}</span>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="docs-contents-btn">☰ Contents</button>
      </div>

      <div className="docs-layout">

        {/* Desktop sidebar */}
        <aside className="docs-sidebar" style={{
          width: 240, flexShrink: 0, position: "sticky", top: 118,
          height: "calc(100vh - 118px)", overflowY: "auto", overflowX: "hidden",
          padding: "28px 0 40px", borderRight: "1px solid rgba(255,255,255,0.05)",
        }}>
          <div style={{ fontSize: 9.5, letterSpacing: 2.5, textTransform: "uppercase", color: "#334155", fontFamily: "'Syne', sans-serif", fontWeight: 700, marginBottom: 14, paddingLeft: 8 }}>On this page</div>
          {sections.map(({ id, label }) => (
            <button key={id} onClick={() => scrollTo(id)} style={{
              display: "block", width: "100%", textAlign: "left",
              background: active === id ? "rgba(0,229,176,0.08)" : "transparent",
              border: "none", borderLeft: `3px solid ${active === id ? "#00e5b0" : "transparent"}`,
              padding: "9px 16px", color: active === id ? "#00e5b0" : "#6b8299",
              fontSize: 13, transition: "all 0.13s ease", fontFamily: "'Syne', sans-serif", cursor: "pointer",
            }}>{label}</button>
          ))}
          <div style={{ marginTop: 40, padding: "20px 0 0 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 9.5, letterSpacing: 2, textTransform: "uppercase", color: "#334155", fontFamily: "'Syne', sans-serif", fontWeight: 700, marginBottom: 12 }}>Resources</div>
            <a href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer" style={{ color: "#6b8299", fontSize: 13, textDecoration: "none", display: "block", padding: "6px 0" }}>↓ Whitepaper PDF</a>
          </div>
        </aside>

        {/* Mobile drawer */}
        {sidebarOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 998 }} onClick={() => setSidebarOpen(false)} />
            <div style={{
              position: "fixed", top: 0, right: 0, bottom: 0, width: "85vw", maxWidth: 320,
              background: "rgba(7,11,17,0.98)", backdropFilter: "blur(12px)",
              borderLeft: "1px solid rgba(255,255,255,0.08)", zIndex: 999,
              overflowY: "auto", padding: "80px 20px 40px",
              boxShadow: "-8px 0 24px rgba(0,0,0,0.6)", color: "#e0e8f4",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#00e5b0" }}>Contents</div>
                <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", color: "#c9d4e3", fontSize: 24, cursor: "pointer" }}>×</button>
              </div>
              {sections.map(({ id, label }) => (
                <button key={id} onClick={() => { scrollTo(id); setSidebarOpen(false); }} style={{
                  display: "block", width: "100%", padding: "14px 16px", marginBottom: 6,
                  background: active === id ? "rgba(0,229,176,0.12)" : "transparent",
                  border: "none", borderRadius: 8, color: active === id ? "#00e5b0" : "#c9d4e3",
                  fontSize: 15, textAlign: "left", fontWeight: active === id ? 600 : 400, cursor: "pointer",
                }}>{label}</button>
              ))}
            </div>
          </>
        )}

        {/* ── MAIN CONTENT ── */}
        <main className="docs-main">

          {/* OVERVIEW */}
          <section id="overview" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Introduction" title="What is Ego Blockchain?"
              subtitle="Ego is a Layer-1 blockchain built for 5G infrastructure, decentralized storage, post-quantum security, and programmable smart contracts. Every reward is tied to real-world utility — coverage measured, data stored, or transaction finalized." />
            <Prose>
              The internet's infrastructure is owned by a small number of centralized entities. Cloud storage is vulnerable to breaches. Wireless networks are gated by carriers. Bitcoin wastes energy on artificial puzzles. Ego solves all three at once — Proof of Coverage rewards 5G signal delivery, Proof of Spacetime rewards continuous data hosting, and Urego smart contracts give any developer the ability to deploy on-chain logic without leaving the Ego Desktop app.
            </Prose>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px,1fr))", gap: 14, margin: "32px 0" }}>
              {[
                { icon: "🖥️", title: "Ego Desktop App", desc: "Non-custodial wallet + encrypted storage + P2P messenger + smart contracts. Native Tauri app — no browser extension needed." },
                { icon: "📜", title: "Urego Smart Contracts", desc: "Rust-inspired language compiling to WASM. Write and compile them in the desktop app's built-in dApp IDE — no toolchain to install. Build tokens, DAOs, escrow, file registries." },
                { icon: "📡", title: "5G Native", desc: "Certified Ego devices passively measure 5G coverage and earn rewards. IoT and cellular-safe by default." },
                { icon: "🔐", title: "Post-Quantum", desc: "ML-KEM (Kyber-768) + ML-DSA (Dilithium-2). Quantum-safe from day one — no migration needed." },
                { icon: "⚡", title: "≥100k TPS", desc: "16-shard mempool with 100 ms micro-slots. 625 TXs per shard per slot. SQLite WAL O(1) block writes. 100k sustained TPS." },
                { icon: "💾", title: "Decentralized Storage", desc: "PoRep + PoSt proofs. Data replicated across geo-diverse independent nodes in RF=3 triads." },
              ].map((c, i) => <InfoCard key={i} icon={c.icon} title={c.title}>{c.desc}</InfoCard>)}
            </div>
            <CalloutBox type="info">
              Ego is in testnet phase. The Desktop app, Urego compiler, HTTP RPC, and JS/TS SDK are all live in the open-source repository. Ego Devices (hardware nodes) are in development.
            </CalloutBox>
          </section>

          <Divider />

          {/* ARCHITECTURE */}
          <section id="architecture" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="System Design" title="Architecture" color="#0066ff"
              subtitle="Five layers, each doing one thing well and anchoring outputs to the layer below via hash commitments." />
            <div style={{ marginBottom: 20 }} className="docs-grid-2">
              <InfoCard icon="📱" title="Layer 1 — Ego Device">Ego-certified hardware. Seals data, generates PoC/PoSt proofs, signs with Dilithium-2 via TPM/SE-backed keys. The only entry point for coverage and storage proofs.</InfoCard>
              <InfoCard icon="🔄" title="Layer 2 — Regional Rollup">Batch-verifies device proofs off-chain, assembles Merkle root commitments, posts one succinct commitment per epoch to the L1 shard.</InfoCard>
              <InfoCard icon="🧱" title="Layer 3 — L1 Shard (16 shards)">HotStuff/Tendermint BFT per shard. Verifies rollup roots, aggregates 0.1s micro-slots, handles cross-shard receipts, executes Urego smart contracts.</InfoCard>
              <InfoCard icon="🌐" title="Layer 4 — Global L1 + DAO">Aggregates shard states, confirms 1–3s finality, distributes EGOC rewards via DRS multipliers, manages protocol parameters via DAO governance.</InfoCard>
            </div>
            <InfoCard icon="🖥️" title="Layer 5 — Ego Desktop App">The user-facing gateway. Tauri (Rust + React) native app. Manages wallet, encrypts files, P2P messaging, deploys and calls Urego contracts — all without a central server.</InfoCard>

            <SubHeading>16-Shard Mempool (100k TPS)</SubHeading>
            <FormulaBlock>{`// Address → Shard routing (FNV-1a hash)
shard = fnv1a(address) % 16

// Micro-slot loop: every 100ms per shard
txs = drain_shard(shard, max=625)
block = mine_batch_db(txs, miner)   // O(1) SQLite WAL INSERT

// Throughput
16 shards × 625 tx/slot ÷ 0.1s = 100,000 TPS sustained
// Chain storage: SQLite WAL — O(1) per block, concurrent reads`}</FormulaBlock>
            <CalloutBox type="success">
              Transactions submitted via the Desktop app or <code style={{ color: "#00e5b0" }}>POST /tx/submit</code> (HTTP RPC) are pushed into the sharded mempool and confirmed within the next 100 ms micro-slot. Blocks are produced at 10/second; global BFT finality in 1–3 seconds.
            </CalloutBox>
          </section>

          <Divider />

          {/* EGO DESKTOP APP */}
          <section id="desktop" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Desktop Application" title="Ego Desktop App" color="#00e5b0"
              subtitle="A native Tauri application (Rust backend + React frontend) that gives every user a self-sovereign gateway to the Ego network. No browser extension, no central server, no custodian." />
            <Prose>
              The Ego Desktop app ships as a signed native binary for Windows, macOS, and Linux. The Rust backend handles all cryptographic operations — Ed25519 + Dilithium-2 keys, AES-256-GCM file encryption, BLAKE2 hashing — while the React frontend provides a minimal dark-theme UI. Nothing leaves your machine unencrypted.
            </Prose>

            <SubHeading>Pages & Features</SubHeading>
            <div style={{ marginBottom: 28 }} className="docs-grid-2">
              <InfoCard icon="👛" title="Wallet">Non-custodial. Send/receive EGOC and EGUSD. Multichain balances (BTC, ETH, SOL…). QR code for address. Pre-Sale Records. Swap via ChangeNow (external pairs) or on-chain oracle (EGOC↔EGUSD). Import wallet from 24-word phrase or raw seed hex.</InfoCard>
              <InfoCard icon="💾" title="EgoSafe — Encrypted Storage">Drag-and-drop AES-256-GCM file encryption. Generate public links (<code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoshare1:</code>) or strictly secure links (<code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoshare2:</code>) encrypted exclusively for the recipient using their Kyber768 public key.</InfoCard>
              <InfoCard icon="💬" title="Messenger">P2P E2E-encrypted chat using Kademlia DHT. Uses Kyber768 for key exchange and Double Ratchet with AES-256-GCM. Features UPnP port mapping with seamless fallback to decentralized community relays if blocked by strict NATs.</InfoCard>
              <InfoCard icon="📜" title="Contracts">Deploy Urego .wasm files. Every node runs a deploy and every call once its block is final, so a contract lives on the network, not on one machine. Read runs a function on your node for free; Send signs a transaction. Activity lists everything the network ran on a contract.</InfoCard>
              <InfoCard icon="🧑‍💻" title="dApp IDE">A full Urego development environment built into the app — Monaco editor, multi-file projects, starter templates, and one-click compile to WASM. No toolchain to install. See <a href="#dapp-ide" style={{ color: "#8b5cf6" }}>dApp IDE</a>.</InfoCard>
              <InfoCard icon="📊" title="Explorer">Real-time Blocks, Transactions, and Tokens tabs. Features in-page search, activity filtering (User vs. Protocol), and user/system transaction breakdowns. Paginated tables (25/50/100 rows) with TX Action labels. No third-party API trust required.</InfoCard>
              <InfoCard icon="💰" title="Earnings">Live session reward counter. Storage, consensus, and coverage earnings per epoch. Rewards only accrue while the app is running.</InfoCard>
              <InfoCard icon="🔒" title="Lock Screen & Security">Secure Lock Screen encrypts your wallet locally. OS-level hardware encryption protects your seed. Optimized UX removes the password requirement for every single transaction while keeping assets fully protected.</InfoCard>
              <InfoCard icon="🥩" title="Staking">Stake EGOC for consensus rewards. APR: ~40% for node stakers, ~20% for general stakers (DAO-tunable). Lock bonuses: +2% at 90d, +5% at 180d, +10% at 365d. Early unstake penalty: 10%.</InfoCard>
              <InfoCard icon="📈" title="Market">Live EGOC price, multichain prices via CoinGecko. Charts and portfolio value in USD.</InfoCard>
              <InfoCard icon="🤝" title="P2P Trade">Buy and sell EGOC, USDT, USDC, ETH, BNB, TRX, POL, SOL and ADA directly with other people for bank transfer, Wise, Revolut or cash. Coins wait in escrow on their own network until the seller confirms the payment. See <a href="#p2p" style={{ color: "#22c55e" }}>P2P Trade &amp; Escrow</a>.</InfoCard>
              <InfoCard icon="🤖" title="Ego AI Assistant">A fully local, offline AI chatbot built directly into the app. Instantly answers questions about Ego's consensus, smart contracts, tokenomics, and EIPs without any external API dependencies.</InfoCard>
              <InfoCard icon="🖥️" title="Compute">Rent out your idle CPU, GPU, and RAM, or book decentralized clusters for AI training and rendering (Ray integration). Handled trustlessly via on-chain escrow.</InfoCard>
              <InfoCard icon="🗳️" title="Governance — DAO">Submit and vote on proposals. Two-type voting: stake power + knowledge power. Community-submitted proposals and knowledge tests. Fully decentralized.</InfoCard>
            </div>

            <SubHeading>Technical Stack</SubHeading>
            <Table
              headers={["Component", "Technology", "Purpose"]}
              rows={[
                ["Backend runtime", "Tauri v1 (Rust)", "Native OS integration, file I/O, crypto, ledger"],
                ["Frontend", "React 18 + TypeScript + Vite", "UI, state management, routing"],
                ["Signatures", "Dilithium-2 (ML-DSA) + Ed25519", "Transaction signing, identity"],
                ["Key exchange", "ML-KEM (Kyber-768)", "Messenger session keys"],
                ["File encryption", "AES-256-GCM", "EgoSafe encrypted storage"],
                ["Hashing", "BLAKE2s-256", "Addresses, CIDs, PIN hashing"],
                ["Address format", "Bech32 egot1... (testnet)", "chain_id=1, AddressType::EOA"],
                ["Ledger", "JSON — %LOCALAPPDATA%/EgoDesktop/ledger.json", "Local chain state"],
              ]}
            />

            <SubHeading>File Sharing Format</SubHeading>
            <FormulaBlock>{`egoshare1:{cid}:{key_nonce_hex}:{base64_filename}:{from_address}

// CID:          egocid1{blake2s_hex_of_plaintext}
// key_nonce_hex: hex(aes256_key_32B || gcm_nonce_12B)

// Import: paste bundle → import_shared_file command
//         backend decrypts → desktop notification fires`}</FormulaBlock>

            <SubHeading>Messenger Bundle Format</SubHeading>
            <FormulaBlock>{`// Contact bundle (copy-paste pairing)
egocontact1:{address}:{ed25519_hex}:{kyber_hex}:{name_b64}:{shared_key_hex}

// Pairing: A generates card → B imports → B approves (name pre-filled) → auto-close → approved

// Message wire format
egomsg1:{from}:{to}:{timestamp}:{type}:{nonce_hex}:{ciphertext_hex}
// type ∈ { "text", "file_bundle", "decrypt_key" }
// Encrypted: AES-256-GCM with per-pair 32-byte shared key`}</FormulaBlock>

            <SubHeading>Email 2FA — Registration & Transactions</SubHeading>
            <Prose>
              All confirmation codes are <strong style={{ color: "#c9d4e3" }}>4 digits + 2 uppercase letters at random positions</strong> (e.g. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>3A8S97</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>4E9Q72</code>). This gives ~101 million combinations — more secure than pure 6-digit codes. Sending EGOC triggers an email to your registered address; the code must be entered in the app to complete the transfer.
            </Prose>
            
            <CalloutBox type="warning">
              <strong>macOS & Windows Security:</strong> We are currently in the process of obtaining official Apple Developer Notarization and a Microsoft EV Code Signing Certificate.
              <br /><br />
              <strong>Mac:</strong> If blocked, right-click the app → Open, or run <code style={{ color: "#fbbf24" }}>xattr -dr com.apple.quarantine "/Applications/Ego Desktop.app"</code>.
              <br />
              <strong>Windows:</strong> If "SmartScreen" appears, click "More Info" → "Run Anyway."
            </CalloutBox>

            <CalloutBox type="info">
              <strong>macOS Keychain Fix:</strong> If you see a "Keyring read error", "User canceled", or "Invalid length" error, open <strong>Keychain Access.app</strong>, search for <code>ego-desktop</code>, and delete the entry. Then, run <code>rm -rf ~/Library/Application\ Support/EgoDesktop</code> in your terminal and restart the app. You will need to re-import your recovery phrase.
            </CalloutBox>

            <Table
              headers={["Rule", "Detail"]}
              rows={[
                ["Code format", "4 digits + 2 uppercase letters at random positions"],
                ["Entropy", "~101,400,000 combinations"],
                ["Expiry", "10 minutes"],
                ["Send limit", "Max 3 emails per address per hour — resets on successful verification"],
                ["Triggers", "Account registration, every outgoing EGOC transaction"],
              ]}
            />
          </section>

          <Divider />

          {/* DAPP IDE */}
          <section id="dapp-ide" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Developer Tooling" title="dApp IDE — Write Urego Inside Ego Desktop" color="#8b5cf6"
              subtitle="A complete Urego development environment built into the desktop app. Write, compile and test a contract without installing Rust, cargo, or the urego CLI." />
            <Prose>
              Open <strong style={{ color: "#c9d4e3" }}>dApp IDE</strong> in the Ego Desktop sidebar. It embeds the Monaco editor — the same editor that powers VS Code — with multi-file projects, syntax highlighting for <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>.urego</code>, and a build log. Compiling calls the <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>urego-compiler</code> crate that ships inside the app, so the WASM it produces is byte-for-byte what the standalone <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>urego</code> CLI produces. There is no toolchain to install and nothing leaves your machine to build.
            </Prose>

            <SubHeading>Starter Templates</SubHeading>
            <Prose>
              Every new project starts from a working contract rather than a blank file. Each template scaffolds <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>src/main.urego</code> plus an <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>ego.toml</code> project manifest, and some add a <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>frontend/index.html</code> you can render in the built-in preview pane.
            </Prose>
            <div style={{ marginBottom: 28 }} className="docs-grid-2">
              <InfoCard icon="📖" title="Guestbook">A complete dApp with a frontend. Anyone can sign; every node keeps the messages. The text travels in the signed call and the contract keeps the count, so the page reads both.</InfoCard>
              <InfoCard icon="👋" title="Hello World">A minimal counter. <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>init()</code>, <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>visit()</code> and <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>get_visits()</code> — the shortest path to a deployed contract.</InfoCard>
              <InfoCard icon="🪙" title="EGO-20 Token">Fungible token with capped supply, mint, burn, and circulating-supply reads. Amounts are u64 uEGOC.</InfoCard>
              <InfoCard icon="🤝" title="Escrow">Locks an amount with a deadline and a one-shot release or refund, guarded by assertions so neither can run twice.</InfoCard>
              <InfoCard icon="🗳️" title="DAO Vote">On-chain yes/no tally with an open/closed flag and a winner read. Emits an event on every vote.</InfoCard>
            </div>

            <SubHeading>Workflow</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="New project">Pick a template, or open an existing project folder from disk (up to 200 files). Projects persist locally between sessions.</StepCard>
              <StepCard number="2" title="Write">Edit <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>src/main.urego</code> in the Monaco editor. Add, rename and delete files in the project tree.</StepCard>
              <StepCard number="3" title="Compile">Compiles the open <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>.urego</code> file to WASM in-process and reports the module size. Compiler errors land in the build log with the message the compiler emitted.</StepCard>
              <StepCard number="4" title="Check the ABI">Every <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>pub fn</code> is extracted into an entrypoint list with argument and return types — this is the ABI stored alongside the deployed contract.</StepCard>
              <StepCard number="5" title="Dry Run">Runs <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>init</code> on your machine against the current chain and shows the address the contract will get. Nothing is sent and nothing is spent, so a contract that fails here never costs a fee.</StepCard>
              <StepCard number="6" title="Deploy">Checks <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>init</code> locally, then signs a deploy transaction (about 0.8 test EGOC; stakers pay a tiny fraction, and the fee is burned). Every node runs <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>init</code> once the block is final, usually within a minute, and the IDE switches to "Live on chain". Init arguments accept plain decimal numbers or raw hex.</StepCard>
            </div>

            <CalloutBox type="info">
              The IDE and the <code style={{ color: "#00e5b0" }}>ego-cli</code> both use the <code style={{ color: "#00e5b0" }}>src/main.urego</code> project layout. The standalone <code style={{ color: "#00e5b0" }}>urego</code> CLI works on single <code style={{ color: "#00e5b0" }}>.uro</code> files instead — same language, same compiler, different entry point. Use whichever fits your workflow.
            </CalloutBox>

            <SubHeading>Live Preview</SubHeading>
            <Prose>
              A project can carry a <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>frontend/index.html</code> alongside its contract. The Preview tab renders it in a sandboxed frame next to the editor and gives the page a <code style={{ color: "#8b5cf6", fontFamily: "monospace" }}>window.ego</code> object tied to the contract named above the preview (filled in automatically after a deploy). Sending always asks you to confirm first.
            </Prose>
            <FormulaBlock>{`ego.contract                   // the contract this page talks to
await ego.read("entries")      // runs a function on your node, free → { value, raw }
await ego.send("sign", "hi")   // asks you, then signs a call (0.6 EGOC) → { txHash }
await ego.activity(50)         // what the network ran on the contract, newest first
await ego.state("key")         // one stored value, as hex
await ego.account()            // your address

// Arguments: text, a number, a list of numbers, or { hex: "…" }`}</FormulaBlock>

            <CalloutBox type="info">
              Writing, compiling, inspecting the ABI and dry runs happen on your machine. Deploys and calls go to the network and run on every node. For now contracts keep their own data and cannot move EGOC: a call that tries is refused on every node and changes nothing.
            </CalloutBox>
          </section>

          <Divider />

          {/* UREGO SMART CONTRACTS */}
          <section id="urego" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Smart Contracts" title="Urego — The Ego Contract Language" color="#8b5cf6"
              subtitle="A Rust-inspired language that compiles to WebAssembly. Write contracts in a familiar syntax and compile them either in the built-in dApp IDE or with the urego CLI, then deploy from the Ego Desktop app." />
            <Prose>
              Urego is not Solidity and is not EVM-compatible — it is a clean, minimal language purpose-built for deterministic on-chain execution. The compiler pipeline: Lexer → Parser → AST → WAT (WebAssembly Text Format) → binary WASM. The entire compiler is open-source Rust.
            </Prose>

            <SubHeading>Language Features</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="📦" title="Types">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>u64</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>i64</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>u32</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>bool</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Address</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>String</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Bytes</code>. Functions return any type or unit <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>()</code>.
              </InfoCard>
              <InfoCard icon="🔧" title="Control Flow">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>if / else</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>while</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>let</code> bindings, assignment, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>return</code>. Arithmetic, comparison, and boolean operators with full precedence climbing.
              </InfoCard>
              <InfoCard icon="🗄️" title="Storage Builtins">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>storage.set("key", value)</code> — persist a u64.<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>storage.get_u64("key")</code> — read a u64 (0 if never set).<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>storage.del("key")</code> — remove a key.<br />
                Keys are fixed names written in the source; values are u64. Text a user sends lives in their signed call and in the contract's activity.
              </InfoCard>
              <InfoCard icon="📣" title="Events & System">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>events.emit("topic", value)</code> — emit a u64 event.<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>sys.caller()</code> — sender Address.<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>sys.block_height()</code> / <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>sys.timestamp()</code>.<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>blake3_hash(data)</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoc_transfer(to, amt)</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>assert(cond, "msg")</code>.
              </InfoCard>
            </div>

            <SubHeading>Compiler Pipeline</SubHeading>
            <FormulaBlock>{`Source (.uro)
  ↓  Lexer          keywords, types, literals, operators → Vec<Token>
  ↓  Parser         recursive descent + precedence climbing → AST
  ↓  Codegen        WAT text: string interning, memory layout, host imports
  ↓  wat::parse_str WAT text → binary WASM bytes
  ↓
Output (.wasm)       deployable via ego-desktop or HTTP RPC`}</FormulaBlock>

            <SubHeading>WASM Memory Layout</SubHeading>
            <FormulaBlock>{`Offset 0    – 4095  : string literal data (all storage key strings)
Offset 4096 – 4103  : 8-byte scratch for u64 r/w
Offset 4104 – 4123  : 20-byte scratch for Address r/w
Offset 8192+        : stack / user data`}</FormulaBlock>

            <SubHeading>Example Contracts</SubHeading>
            <FormulaBlock>{`// ── Fungible Token ──────────────────────────────────────────
contract Token {
    fn init(supply: u64) {
        storage.set("supply", supply);
        storage.set("minted", 0);
    }
    fn mint(amount: u64) {
        let minted: u64 = storage.get_u64("minted");
        let supply: u64 = storage.get_u64("supply");
        assert(minted + amount <= supply, "exceeds supply");
        storage.set("minted", minted + amount);
        events.emit("minted", amount);
    }
    fn total_supply() -> u64 { return storage.get_u64("supply"); }
}

// ── Escrow ────────────────────────────────────────────────────
contract Escrow {
    fn init(amount: u64) {
        storage.set("amount", amount);
        storage.set("released", 0);
    }
    fn release(recipient: Address) {
        let amount: u64 = storage.get_u64("amount");
        assert(storage.get_u64("released") == 0, "already released");
        egoc_transfer(recipient, amount);
        storage.set("released", 1);
        events.emit("released", amount);
    }
}

// ── Counter ───────────────────────────────────────────────────
contract Counter {
    fn init(start: u64) { storage.set("count", start); }
    fn increment() {
        let v: u64 = storage.get_u64("count");
        storage.set("count", v + 1);
    }
    fn get() -> u64 { return storage.get_u64("count"); }
}

// ── File Registry ────────────────────────────────────────────
contract FileRegistry {
    fn register(cid_hash: u64) {
        let owner: Address = sys.caller();
        storage.set("file_owner", cid_hash);
        events.emit("registered", sys.timestamp());
    }
    fn get_owner_ts() -> u64 { return storage.get_u64("file_owner"); }
}`}</FormulaBlock>

            <SubHeading>Host Function Imports</SubHeading>
            <Table
              headers={["Host Function", "Signature", "Description"]}
              rows={[
                ["storage_get", "(prefix_ptr, prefix_len, key_ptr, key_len, out_ptr) → i32", "Copy a stored value to out_ptr; returns its length, or -1 if unset"],
                ["storage_set", "(prefix_ptr, prefix_len, key_ptr, key_len, val_ptr, val_len)", "Store a value (keys up to 256 bytes, values up to 64 KB)"],
                ["storage_del", "(prefix_ptr, prefix_len, key_ptr, key_len)", "Delete a key"],
                ["events_emit", "(topic_ptr, topic_len, data_ptr, data_len)", "Emit an event, recorded in the contract's activity"],
                ["blake3_hash", "(data_ptr, data_len, out_ptr)", "Write the 32-byte BLAKE3 hash to out_ptr"],
                ["sys_caller", "(out_ptr) → i32", "Write the caller's address text to memory; returns its length"],
                ["sys_block_height", "() → i64", "Height of the block the call is in"],
                ["sys_timestamp", "() → i64", "Time of the block the call is in, in seconds"],
                ["egoc_transfer", "(to_ptr, to_len, amount: i64)", "Not enabled yet: a call that uses it is refused and changes nothing"],
                ["urego_assert", "(cond: i32)", "Stops the call when cond is 0; nothing it changed is kept"],
              ]}
            />
          </section>

          <Divider />

          {/* BUILDING A CONTRACT */}
          <section id="smart-contract-guide" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Developer Guide" title="Building & Deploying a Smart Contract" color="#f59e0b"
              subtitle="Two routes to the same WASM: the dApp IDE inside Ego Desktop, or the urego CLI." />

            <Prose>
              Both routes run the same compiler and produce the same module. The <strong style={{ color: "#c9d4e3" }}>dApp IDE</strong> is the fastest way in — nothing to install, and templates to start from. The <strong style={{ color: "#c9d4e3" }}>CLI</strong> is the one to reach for when you want contracts in source control, built by a script or in CI.
            </Prose>

            <SubHeading>Route A — dApp IDE (no install)</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Open the IDE">Ego Desktop → dApp IDE in the sidebar → pick a template (start with Guestbook).</StepCard>
              <StepCard number="2" title="Edit and compile">Write <code style={{ color: "#f59e0b", fontFamily: "monospace" }}>src/main.urego</code> → Compile. Errors appear in the build log; success reports the WASM size.</StepCard>
              <StepCard number="3" title="Dry Run">Runs <code style={{ color: "#f59e0b", fontFamily: "monospace" }}>init</code> locally and shows the future address. Nothing is sent.</StepCard>
              <StepCard number="4" title="Deploy">Signs a deploy transaction. Every node creates the contract once the block is final; open the frontend in Preview to use it.</StepCard>
            </div>
            <CalloutBox type="success">
              Full detail on the editor, templates and preview pane: <a href="#dapp-ide" style={{ color: "#00e5b0" }}>dApp IDE</a>.
            </CalloutBox>

            <SubHeading>Route B — urego CLI</SubHeading>
            <SubHeading>1 — Install the urego compiler</SubHeading>
            <FormulaBlock>{`git clone https://github.com/ego-blockchain/ego-blockchain-
cd ego-blockchain
cargo build -p urego --release
# Binary: target/release/urego (macOS/Linux) or target/release/urego.exe (Windows)`}</FormulaBlock>

            <SubHeading>2 — Scaffold and write your contract</SubHeading>
            <FormulaBlock>{`urego new MyToken          # creates mytoken.uro with a starter template

# Edit mytoken.uro:
contract MyToken {
    fn init(supply: u64) {
        storage.set("supply", supply);
        storage.set("minted", 0);
    }
    fn mint(amount: u64) {
        let minted: u64 = storage.get_u64("minted");
        let supply: u64 = storage.get_u64("supply");
        assert(minted + amount <= supply, "exceeds supply");
        storage.set("minted", minted + amount);
        events.emit("mint", amount);
    }
    fn total_supply() -> u64 { return storage.get_u64("supply"); }
}`}</FormulaBlock>

            <SubHeading>3 — Compile to WASM</SubHeading>
            <FormulaBlock>{`urego check mytoken.uro    # type-check only, no output
urego wat   mytoken.uro    # inspect generated WAT (text format)
urego build mytoken.uro    # compile → mytoken.wasm`}</FormulaBlock>

            <SubHeading>4 — Deploy from Ego Desktop or HTTP RPC</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Open Contracts page">Ego Desktop → Contracts in sidebar.</StepCard>
              <StepCard number="2" title="Deploy tab">Drag in mytoken.wasm → enter contract name → Deploy.</StepCard>
              <StepCard number="3" title="Wait ~50ms">TX goes to sharded mempool → confirmed in next batch window.</StepCard>
              <StepCard number="4" title="Interact">Interact tab → select contract → enter entrypoint + args → Call.</StepCard>
              <StepCard number="5" title="Read state">Read State tab → query any storage key for on-chain values.</StepCard>
            </div>

            <SubHeading>Deploy via TypeScript SDK</SubHeading>
            <FormulaBlock>{`import { EgoClient } from "@ego-blockchain/sdk";
import { readFileSync } from "fs";

const client = new EgoClient({ rpcUrl: "http://localhost:8545" });

const { block_height } = await client.health();
console.log("Block height:", block_height);

const wasm = readFileSync("mytoken.wasm");
const result = await client.submitTx({
  from:    "0xYOUR_ADDRESS",
  to:      "0x0000000000000000000000000000000000000000",
  amount:  "0",
  nonce:   0,
  payload: { type: "deploy", wasm: Array.from(wasm), init_args: [1_000_000] },
});
console.log("TX:", result.tx_hash);

const confirmedAt = await client.waitForBlocks(1);
console.log("Confirmed at block", confirmedAt);`}</FormulaBlock>

            <CalloutBox type="success">
              Install the SDK: <code style={{ color: "#00e5b0" }}>npm install @ego-blockchain/sdk</code>. Wraps all HTTP RPC endpoints with TypeScript types.
            </CalloutBox>

            <SubHeading>What can you build?</SubHeading>
            <div style={{ }} className="docs-grid-2">
              <InfoCard icon="🪙" title="Fungible Token">Capped-supply token with per-address balances. Emit events on mint. Use storage to map balances.</InfoCard>
              <InfoCard icon="🗳️" title="DAO Voting">Store proposals and vote tallies on-chain. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>sys.caller()</code> identifies voters. Emit events when proposals pass quorum.</InfoCard>
              <InfoCard icon="🔐" title="Escrow">Lock EGOC with <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoc_transfer</code>. Release to recipient only after conditions are met. One-time flag in storage prevents double-release.</InfoCard>
              <InfoCard icon="📁" title="File Registry">Store CID hashes of encrypted files. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>blake3_hash</code> for integrity. Map file CIDs to owner addresses on-chain.</InfoCard>
            </div>
          </section>

          <Divider />

          {/* BLOCK SPEC */}
          <section id="blocks" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Block Format" title="Block Specification" color="#06b6d4"
              subtitle="~4 KB headers commit to all data layers via Merkle roots. Finality the moment a QC forms." />
            <Table
              headers={["Field", "Type", "Description"]}
              rows={[
                ["shard_id", "u16", "Which shard this block belongs to"],
                ["height", "u64", "Monotonically increasing block height"],
                ["epoch", "u64", "Epoch number"],
                ["prev_hash", "Hash[32]", "BLAKE2s-256 of previous header"],
                ["proposer", "Address[20]", "Dilithium-2 address of block proposer"],
                ["tx_root", "Hash[32]", "Merkle root over all transactions"],
                ["state_root", "Hash[32]", "Post-execution state trie root"],
                ["receipts_root", "Hash[32]", "Merkle root over execution receipts"],
                ["events_root_post", "Hash[32]", "Merkle root over PoSt/PoRep events"],
                ["events_root_poc", "Hash[32]", "Merkle root over PoC events"],
                ["rollup_root", "Hash[32]", "Root of L2 rollup commits"],
                ["da_root", "Hash[32]", "Data availability blob commitments"],
                ["vrf_output", "Hash[32]", "Seeds next epoch randomness"],
                ["timestamp", "u64", "Unix timestamp (ms)"],
                ["signature", "Bytes[~2420]", "Dilithium-2 signature by proposer"],
                ["qc", "QuorumCert", "Quorum certificate from previous round"],
              ]}
            />
            <Table
              headers={["Metric", "Value"]}
              rows={[
                ["Header size", "~4 KB (fixed)"],
                ["Micro-slot duration", "~0.1s"],
                ["Global finality", "1–3s"],
                ["Max TPS", "≥100,000 across 16 shards"],
                ["Dilithium-2 sig size", "~2,420 bytes"],
              ]}
            />
          </section>

          <Divider />

          {/* POC */}
          <section id="poc" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Coverage Proofs" title="Proof of Coverage (PoC)" color="#0066ff"
              subtitle="Cryptographic proof that a certified Ego device provided real, measurable 5G signal at a specific time and location — backed by independent witnesses, GNSS position, and a time-bound co-beacon signature." />

            <Prose>
              Coverage proofs are the hardest aspect of the Ego protocol to fake. Unlike mining — which can be done anywhere — a PoC event requires a physical device at a real location to passively receive genuine 5G signals from a live carrier tower, and have those measurements corroborated by independent witnesses who were also physically present in the same H3 hex cell.
            </Prose>

            <SubHeading>What Gets Measured</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="📶" title="RF Signal Metrics (3GPP)">
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2.1 }}>
                  <li><strong style={{ color: "#c9d4e3" }}>RSRP</strong> — Reference Signal Received Power (dBm). Primary signal strength.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>RSRQ</strong> — Reference Signal Received Quality (dB). Accounts for interference.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>SINR</strong> — Signal to Interference + Noise Ratio. Channel quality indicator.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>PCI</strong> — Physical Cell ID. Identifies the serving cell.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>NR-ARFCN</strong> — Absolute Radio Frequency Channel Number for 5G NR.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>SSB / Beam Index</strong> — Synchronization signal block (optional).</li>
                  <li><strong style={{ color: "#c9d4e3" }}>Timing Advance</strong> — Propagation delay estimate when modem exposes it.</li>
                </ul>
              </InfoCard>
              <InfoCard icon="📍" title="Location & Time Binding">
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2.1 }}>
                  <li><strong style={{ color: "#c9d4e3" }}>GNSS fix</strong> — Latitude, longitude, altitude, DOP quality score.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>UTC timestamp</strong> — Cryptographically bound to the challenge window nonce.</li>
                  <li><strong style={{ color: "#c9d4e3" }}>Co-beacon</strong> — BLE/Wi-Fi side-channel carrying a signed <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>{"{nonce16, time32, beacon_id}"}</code>. An attacker replaying RF recordings must also forge the co-beacon — which requires the device's TPM private key.</li>
                </ul>
              </InfoCard>
            </div>

            <SubHeading>Challenge Window Protocol</SubHeading>
            <Prose>
              Challenges are not continuous — they fire in short, randomised windows to make them unpredictable and unfakeable. The on-chain scheduler selects one device per H3 hex cell per window using epoch-derived VRF randomness.
            </Prose>
            <FormulaBlock>{`// Epoch randomness
R_e = H("ego/poc/v1" || vrf_output || region_id || epoch || slot)

// Device selection per H3 cell
selected_device = devices_in_cell[ R_e mod |devices_in_cell| ]

// Challenge window timing
window_start  = epoch_start + slot_offset
window_end    = window_start + W           // W = 10 seconds
announce_at   = window_start − 1500ms     // 1.5s lead time

// Beacon announcement (Dilithium-2 signed by proposer)
BeaconAnnouncement {
  beacon_id, nonce16, epoch, slot,
  window_start, window_end,
  nr_band, pci_hint,
  signature: Dilithium-2
}`}</FormulaBlock>

            <SubHeading>Witness Requirements</SubHeading>
            <Prose>
              A PoC event needs at least <strong style={{ color: "#c9d4e3" }}>3 independent witnesses</strong> to be accepted. "Independent" is defined strictly:
            </Prose>
            <div style={{ marginBottom: 24 }} className="docs-grid-3">
              {[
                { icon: "🗺️", title: "≥2 H3 cells", body: "Witnesses must be spread across at least 2 distinct H3 hex cells. Witnesses all within the same cell get down-weighted." },
                { icon: "👛", title: "≥2 wallet accounts", body: "Witnesses must use at least 2 distinct on-chain wallet addresses with no shared transaction history." },
                { icon: "🌐", title: "No shared IP", body: "Witnesses sharing an IP subnet history within the past 30 days are flagged as potentially co-located and rejected." },
              ].map((c, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(0,102,255,0.15)", borderRadius: 10, padding: "16px 14px" }}>
                  <div style={{ fontSize: 18, marginBottom: 8 }}>{c.icon}</div>
                  <div style={{ color: "#e0e8f4", fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{c.title}</div>
                  <div style={{ color: "#5a6a80", fontSize: 12.5, lineHeight: 1.7 }}>{c.body}</div>
                </div>
              ))}
            </div>

            <SubHeading>Quality Score Computation</SubHeading>
            <Prose>
              Raw RF measurements are converted to a normalised quality score <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>q ∈ [0.5, 1.0]</code> using 3GPP 38.901 path-loss models. The aggregator fits a path-loss curve to the witness set and penalises geometric inconsistencies.
            </Prose>
            <FormulaBlock>{`// 3GPP 38.901 Urban Macro path-loss (simplified)
PL(d) = 28.0 + 22·log10(d) + 20·log10(f_c)

// Fit quality
RMSE  = sqrt( mean( (PL_measured_i − PL_model_i)² ) )
q_fit = max(0, 1 − RMSE / τ)      // τ ≈ 8 dB threshold

// Local Density Multiplier — fairness in dense deployments
LDM = max(0.40, 1.0 − 0.10 · (collocated_devices − 1))
// −10% per extra co-located device, floor at 0.40

// Witness confidence factor
witness_confidence = f(witness_count, diversity_score)

// Final quality score (feeds Coverage Bucket in DRS)
q = q_fit · LDM · witness_confidence
q = clamp(q, 0.50, 1.0)           // minimum accepted score: 0.50`}</FormulaBlock>

            <SubHeading>Anti-Gaming Measures</SubHeading>
            <div style={{ marginBottom: 16 }} className="docs-grid-2">
              <InfoCard icon="🔄" title="Replay Protection">
                Each challenge uses a unique <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>(beacon_id, nonce16, epoch)</code> triple. Stale or duplicate nonces are rejected. The co-beacon's signed timestamp ensures measurement was captured during the live window, not replayed from a recording.
              </InfoCard>
              <InfoCard icon="📐" title="Geometry Validation">
                The aggregator runs 3GPP path-loss fitting across all witnesses. Physically impossible combinations — e.g. high RSRP from a cell 50 km away — are rejected. Outliers removed with robust regression before scoring.
              </InfoCard>
              <InfoCard icon="🚫" title="Self-Witness Loop Detection">
                Devices that appear as both challenger and witness in the same event are rejected. On-chain account graphs are analysed for rings of mutual witnesses — correlated sets are down-weighted as a group.
              </InfoCard>
              <InfoCard icon="📡" title="Cellular-Safe Scanning">
                RF scanning runs at sub-1 Hz by default. The device acts as a passive UE listener only — it never transmits on licensed spectrum. Carrier ToS compliance is enforced at the firmware level.
              </InfoCard>
            </div>

            <SubHeading>On-Chain PoCEvent Structure</SubHeading>
            <FormulaBlock>{`PoCEvent {
  beacon_id:          Hash[32]    // identifies the challenge window
  device_addr:        Address     // challenger device
  epoch:              u64
  slot:               u64
  h3_cell:            String      // H3 hex cell ID (resolution 7–8)
  quality_score:      u32         // q × 10000 (fixed-point, 4 decimals)
  witness_count:      u8
  witnesses_root:     Hash[32]    // Merkle root over WitnessReport set
  gnss_lat:           i64         // latitude  × 10⁷ (fixed-point)
  gnss_lon:           i64         // longitude × 10⁷
  gnss_dop:           u16         // dilution of precision × 100
  nr_band:            u16         // 5G NR band number
  rsrp_avg:           i16         // avg RSRP in 0.1 dBm steps
  sinr_avg:           i16         // avg SINR in 0.1 dB steps
  timestamp:          u64         // ms since Unix epoch
  signature:          Dilithium2  // device TPM signature
}
// Anchored in block header via events_root_poc`}</FormulaBlock>

            <CalloutBox type="info">
              PoC events earn from the <strong>Coverage Bucket (20% of block reward)</strong>. The payout per event is: <code style={{ color: "#00e5b0" }}>coverage_reward = base_rate × q × DRS_multiplier_m</code>. Nodes with consistent high-quality scores earn up to 1.3× the base rate.
            </CalloutBox>
          </section>

          <Divider />

          {/* POST */}
          <section id="post" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Storage Proofs" title="Proof of Spacetime (PoSt)" color="#8b5cf6"
              subtitle="Continuous cryptographic proof that a storage node actively holds committed data across every proving window — not just at sealing time." />

            <Prose>
              Unlike Proof of Replication (which proves a one-time snapshot), PoSt proves that a node has <em>continuously held</em> the data across multiple proving windows. The challenge is derived from VRF randomness that did not exist when the data was first stored — so nodes cannot pre-compute responses or fake long-term storage.
            </Prose>

            <SubHeading>Why PoSt Is Hard to Fake</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-3">
              {[
                { icon: "🎲", title: "Unpredictable Challenges", body: "Challenge leaf indices are derived from VRF output that is only known after the previous block is finalised. A node that deletes data cannot re-generate the response in time." },
                { icon: "🌳", title: "Merkle Branch Opening", body: "The node must open specific Merkle branches of the sealed replica. Without the actual sealed data on disk, the correct branch hashes cannot be computed." },
                { icon: "⏱️", title: "Tight SLA Window", body: "Proofs must be submitted within 3–10 minutes of the challenge. There is no time to re-seal data from scratch if it was deleted — sealing takes hours." },
              ].map((c, i) => (
                <div key={i} style={{ background: "rgba(139,92,246,0.05)", border: "1px solid rgba(139,92,246,0.15)", borderRadius: 10, padding: "16px 14px" }}>
                  <div style={{ fontSize: 18, marginBottom: 8 }}>{c.icon}</div>
                  <div style={{ color: "#e0e8f4", fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{c.title}</div>
                  <div style={{ color: "#5a6a80", fontSize: 12.5, lineHeight: 1.7 }}>{c.body}</div>
                </div>
              ))}
            </div>

            <SubHeading>WindowPoSt Protocol — Step by Step</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Epoch Randomness Derived">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>R_e = H("ego/post/v1" || vrf_output || deal_id || epoch)</code>. This value is unpredictable at sealing time. It is derived fresh each epoch from the current block proposer's VRF output.
              </StepCard>
              <StepCard number="2" title="Challenged Leaf Indices Computed">
                For each sector in the proving partition: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>index_i = PRF(replica_id, R_e || i) mod N_leaves</code>. Different replica_id values produce different challenges even within the same epoch — sectors cannot share proofs.
              </StepCard>
              <StepCard number="3" title="Merkle Branches Opened">
                The node opens Merkle branches from the sampled leaf indices up to CommR (the sealed replica root committed on-chain at deal creation). This requires reading the actual sealed data from disk.
              </StepCard>
              <StepCard number="4" title="Optional SNARK Aggregation">
                For large sector counts, the node can aggregate multiple partition proofs into a single SNARK to reduce on-chain verification cost. The SNARK is computed off-chain; only the compact proof is submitted.
              </StepCard>
              <StepCard number="5" title="PoStEvent Submitted On-Chain">
                The node submits a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>PoStEvent</code> within the SLA window Δ (3–10 min). The event contains the proof CID, deal ID, epoch, and partition IDs. Validators verify the proof against the on-chain CommR.
              </StepCard>
              <StepCard number="6" title="Reward or Penalty">
                Valid proof → full storage bucket reward for that epoch. Missing proof → zero reward, miss counter incremented. Three consecutive misses → slashing (collateral burned, deal invalidated, repair job queued to restore RF=3).
              </StepCard>
            </div>

            <SubHeading>PoSt Proof Structure</SubHeading>
            <FormulaBlock>{`// Per-epoch challenge derivation
R_e = H("ego/post/v1" || vrf_output || deal_id || epoch)

// Per-sector leaf sampling
for i in 0..num_challenges:
    leaf_index[i] = PRF(replica_id, R_e || i) mod N_leaves

// Merkle branch opening
branch[i] = merkle_open(sealed_data, leaf_index[i])
// branch[i] is a path from leaf to CommR

// PoStEvent submitted on-chain
PoStEvent {
  deal_id:        Hash[32]     // storage deal this proof covers
  epoch:          u64
  partition_ids:  Vec<u32>     // which sector partitions were proven
  proof_cid:      String       // CID of off-chain proof bundle
  proof_type:     u8           // 0=Merkle, 1=SNARK-aggregated
  leaves_sampled: u16          // number of leaf challenges
  timestamp:      u64
  signature:      Dilithium2   // storage node signature
}
// Anchored in block header via events_root_post`}</FormulaBlock>

            <SubHeading>Sector Triad (RF=3)</SubHeading>
            <Prose>
              Every storage deal is replicated across exactly 3 storage nodes forming a <strong style={{ color: "#c9d4e3" }}>triad</strong>. The triad members must be in distinct geographic regions and distinct H3 cells to prevent correlated hardware failure. Each member independently seals its own replica (different replica_id) and independently submits PoSt proofs.
            </Prose>
            <FormulaBlock>{`Triad {
  group_id:      String       // unique triad identifier
  primary:       TriadMember  // geographic region A
  replica_a:     TriadMember  // geographic region B (≠ A)
  replica_b:     TriadMember  // geographic region C (≠ A, ≠ B)
  diversity_score: f64        // 0–1, measures geographic spread
}

TriadMember {
  node_id:           Address  // storage node address
  sector_id:         Hash     // sector storing this replica
  replica_id:        Hash     // unique replica identity
  h3_cell:           String   // H3 cell of the node
  region:            String   // geographic region label
  health_score:      u64      // consecutive PoSt passes
  consecutive_misses: u32
}`}</FormulaBlock>

            <SubHeading>Parameters</SubHeading>
            <Table
              headers={["Parameter", "Value", "Notes"]}
              rows={[
                ["Sector size", "32 GiB / 64 GiB", "Fixed sealed unit; cannot change after sealing"],
                ["Proving window", "Every epoch", "Challenges a partition subset each epoch"],
                ["SLA window Δ", "3–10 min", "Deadline after window opens for proof submission"],
                ["Triad replication factor", "RF = 3", "One primary + two geo-diverse replicas"],
                ["Miss penalty", "0% storage rewards", "Full epoch miss = zero storage bucket reward"],
                ["Consecutive miss threshold", "3 windows", "Triggers slashing + automated repair job"],
                ["Slashing amount", "Proportional to deal size", "Burned from locked collateral"],
                ["SNARK aggregation", "Optional", "Reduces on-chain verification cost for many sectors"],
                ["Leaf challenges per sector", "40–280 leaves", "More leaves = higher security, more compute"],
              ]}
            />

            <CalloutBox type="warning">
              Missing a PoSt window does not immediately slash — it just earns zero storage rewards for that epoch and increments the miss counter. Slashing only triggers after <strong>3 consecutive misses</strong>. This gives honest nodes a chance to recover from transient outages without losing their collateral.
            </CalloutBox>
          </section>

          <Divider />

          {/* POREP */}
          <section id="porep" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Storage Proofs" title="Proof of Replication (PoRep)" color="#f59e0b"
              subtitle="One-time proof that a storage node has created a unique, physically distinct replica of a piece of data — permanently bound to its specific identity." />

            <Prose>
              The core insight of PoRep is <em>replica uniqueness</em>. The sealing process transforms the original data into a replica that encodes the node's identity (public key) and a random nonce. Even if two nodes hold identical original data, their replicas are cryptographically distinct. This prevents the <strong style={{ color: "#c9d4e3" }}>Sybil storage attack</strong> — one physical disk pretending to be many independent storage providers.
            </Prose>

            <SubHeading>Why Sealing Is Intentionally Slow</SubHeading>
            <Prose>
              The SDR (Stacked Directed-Randomness) encoding algorithm requires sequential computation that cannot be fully parallelised across graph layers. This creates a natural time-space tradeoff: generating a fake PoRep proof requires as much real compute as generating a genuine one — you cannot shortcut the sealing by having faster hardware, because the bottleneck is sequential graph traversal.
            </Prose>

            <SubHeading>Sealing Pipeline</SubHeading>
            <FormulaBlock>{`// Step 1 — Derive a unique identity for this replica
replica_id = H("ego/replica/v1" || piece_id || node_pk || nonce32)
// Different node_pk  → different replica_id → different sealed data
// Different nonce32  → cannot reuse the same sealed replica for two deals

// Step 2 — SDR Encode (Stacked Directed-Randomness)
D_encoded = SDR_encode(D_original, replica_id)
// Sequential graph traversal: layer L depends on layer L-1
// Cannot be parallelised — sealing takes hours for a 32 GiB sector

// Step 3 — Compute Commitments
CommD = merkle_root(D_original)    // data commitment (original content)
CommR = merkle_root(D_encoded)     // replica commitment (sealed content)

// Step 4 — Generate Zero-Knowledge Proof
π_porep = ZK_prove(
  public:  replica_id, CommD, CommR,
  private: D_encoded, SDR_graph_paths
)
// Proves encoding was done correctly without revealing raw data

// Step 5 — Publish PoRepEvent
PoRepEvent {
  piece_id:         Hash[32]    // content-addressed ID of original data
  node_pk:          PublicKey   // storage node's Dilithium-2 public key
  CommD:            Hash[32]    // original data Merkle root
  CommR:            Hash[32]    // sealed replica Merkle root (stored on-chain)
  proof_cid:        String      // CID of off-chain ZK proof bundle
  nonce32:          Bytes[32]   // randomness binding this replica to this deal
  sector_size:      u64         // 32 GiB or 64 GiB
  timestamp:        u64
  signature:        Dilithium2  // node signature
}
// Anchored in block header via events_root_post`}</FormulaBlock>

            <SubHeading>Commitments Explained</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-3">
              <InfoCard icon="📄" title="CommD — Data Commitment" accent="#f59e0b">
                Merkle root over the <em>original, unsealed</em> data. Used to verify that the node is storing the correct content and can reconstruct the original on retrieval demand. Checked against the client's content hash at deal creation.
              </InfoCard>
              <InfoCard icon="🔒" title="CommR — Replica Commitment" accent="#f59e0b">
                Merkle root over the <em>sealed, node-specific</em> replica. This is the value stored on-chain in the deal record. PoSt challenges open Merkle branches against CommR — so without the actual sealed data, PoSt proofs cannot be generated.
              </InfoCard>
              <InfoCard icon="🧮" title="ZK Proof (π)" accent="#f59e0b">
                Zero-knowledge proof binding CommD, CommR, and replica_id together. Proves the SDR encoding was performed correctly without revealing the raw data contents. The proof CID is posted off-chain; only its hash is committed on-chain.
              </InfoCard>
            </div>

            <SubHeading>Replica Uniqueness Guarantee</SubHeading>
            <FormulaBlock>{`// Two nodes storing the same file produce DIFFERENT replicas:
Node A:  replica_id_A = H("ego/replica/v1" || cid || pk_A || nonce_A)
Node B:  replica_id_B = H("ego/replica/v1" || cid || pk_B || nonce_B)

SDR_encode(data, replica_id_A)  ≠  SDR_encode(data, replica_id_B)
CommR_A                          ≠  CommR_B

// If Node A tries to submit CommR_A as its own proof for a different deal:
// The chain checks: H("ego/replica/v1" || new_deal_cid || pk_A || nonce_A)
// This produces a different replica_id → different expected CommR → proof rejected

// Result: one physical disk cannot service multiple independent storage deals
// without performing the full SDR sealing for each one`}</FormulaBlock>

            <SubHeading>PoRep vs PoSt — Key Difference</SubHeading>
            <Table
              headers={["Property", "PoRep", "PoSt"]}
              rows={[
                ["When it runs", "Once at deal creation (sealing)", "Every epoch for the full deal duration"],
                ["What it proves", "A unique replica was created", "The replica is still on disk right now"],
                ["Challenge type", "Fixed (encoding correctness)", "Random leaf indices from VRF (unpredictable)"],
                ["Compute cost", "Very high (SDR sealing, hours)", "Moderate (Merkle branch opens, minutes)"],
                ["On-chain footprint", "CommD + CommR stored permanently", "PoStEvent submitted each epoch"],
                ["Failure consequence", "Cannot activate deal", "Zero rewards + miss counter (slashing at 3)"],
              ]}
            />

            <CalloutBox type="success">
              PoRep is run <strong>once per deal</strong> at sealing time and its result (CommR) lives on-chain forever. PoSt runs <strong>every epoch</strong> and checks that the node still holds the sealed data by challenging it with new random leaf indices it could not have pre-computed.
            </CalloutBox>
          </section>

          <Divider />

          {/* PQC */}
          <section id="pqc" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Cryptography" title="Post-Quantum Cryptography" color="#00e5b0"
              subtitle="NIST-standardized PQ algorithms throughout the entire protocol stack." />
            <Table
              headers={["Algorithm", "NIST Standard", "Role"]}
              rows={[
                ["ML-KEM (Kyber-768)", "FIPS 203", "Key encapsulation — messenger, content keys"],
                ["ML-DSA (Dilithium-2)", "FIPS 204", "Signatures — transactions, blocks, PoC/PoSt, identity"],
                ["SLH-DSA (SPHINCS+)", "FIPS 205", "Hash-based sigs — epoch anchors, firmware, DRS checkpoints"],
                ["XChaCha20-Poly1305", "AEAD", "Symmetric encryption — file payloads, messenger"],
                ["BLAKE2s-256", "Hash", "Block IDs, Merkle trees, address derivation, CIDs"],
              ]}
            />
            <FormulaBlock>{`// Address derivation
address = versioned_blake2s("ego/addr/v1" || dilithium_pk)[0..20]
encoded = bech32m_encode("ego", address)   // e.g. ego1qxy2kgdyg...`}</FormulaBlock>
          </section>

          <Divider />

          {/* DRS */}
          <section id="drs" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Reward Mechanism" title="Dynamic Reputation Score" color="#ec4899"
              subtitle="Coverage opens the door, storage decides the share. Every input is committed on-chain, so every node computes the same score from the same blocks." />
            <Prose>
              A validator&apos;s influence is earned by what it contributes, not by what it owns. Coverage is what a node can prove on its first day, before anyone has uploaded a byte, so it is what lets a network with no storage customers still weigh its validators by contribution rather than by who arrived first. Storage raises the share from there, because storage is the part the network ultimately needs.
            </Prose>
            <FormulaBlock>{`weight = 1                       seat held
       + proven_witnesses         peers that witnessed this node's beacon
       + 3 × proven_GB            storage proved against a chain challenge
       + staked_EGOC / 2          stake, deliberately the smallest term

Reputation view (Earn tab):
  raw = coverage × 3 + stake × 0.5
  drs = max(ln(1 + raw), 0.01)  ×  3 if new to the network

Concentration cap:
  no validator may hold more than half of what every other
  validator holds combined — which keeps it under a third
  of the total once the sum is recomputed`}</FormulaBlock>

            <SubHeading>What each unit of contribution is worth</SubHeading>
            <div style={{ maxWidth: 520, margin: "8px 0 32px" }}>
              <WeightBar label="Storage — per proven GB" value="3.0" pct={100} />
              <WeightBar label="Coverage — per witnessing peer" value="1.0" pct={33} color="#0066ff" />
              <WeightBar label="Holding a seat" value="1.0" pct={33} color="#8b5cf6" />
              <WeightBar label="Stake — per EGOC" value="0.5" pct={17} color="#f59e0b" />
              <WeightBar label="Blocks produced" value="0.0" pct={0} color="#ec4899" />
            </div>

            <SubHeading>Why stake counts least</SubHeading>
            <Prose>
              Stake is divided by two while coverage counts one for one and storage three for one. That ordering is deliberate: stake proves only that someone had coins, which is the one input a wealthy newcomer can buy outright. Coverage and storage cost real hardware, real uptime, and real peers willing to vouch that the node answered.
            </Prose>
            <Prose>
              Block production is deliberately <strong>not</strong> an input. Paying weight for producing blocks would let whoever starts ahead lead more often, gain weight, and lead more again, until one node decides everything.
            </Prose>

            <SubHeading>Proving coverage</SubHeading>
            <Prose>
              Coverage is proved the way storage is. A node&apos;s beacon identifier is derived from a committed block hash, so it cannot answer a question it set itself, and it cannot know the answer before that block existed. Peers that hear the beacon sign a witness receipt, and those receipts are committed on-chain as a <code style={{ color: "#00e5b0" }}>poc_proof</code> transaction that every node verifies identically.
            </Prose>
            <Table
              headers={["Rule", "Value", "Why"]}
              rows={[
                ["Witness carries its own key", "ed25519 public key + signature", "The address must derive from that key, so the proof needs no gossip to check and two nodes cannot disagree about it"],
                ["Self-witnessing", "refused", "A node cannot vouch for its own beacon"],
                ["Duplicate witnesses", "refused", "Counted once, by address and by machine"],
                ["Witnesses per proof", "32 maximum", "A node reaching thousands of peers must not carry the committee alone"],
                ["Challenge age", "100 blocks", "Old enough that every node has the block, new enough that the answer could not be prepared"],
                ["Proof lifetime", "5,000 blocks", "Coverage is a claim about now; a node cannot prove reachability once and coast"],
              ]}
            />
            <CalloutBox type="warning">
              Coverage proofs are Sybil-<em>resistant</em>, not Sybil-proof. Helium leans on radio physics: you cannot fake hearing a transmission from five kilometres away. Ego has no physics to lean on, and the machine identifier in a witness receipt is self-reported, so a determined operator can run several identities on one box. The 32-witness ceiling and the concentration cap bound the damage; they do not eliminate it.
            </CalloutBox>
          </section>

          <Divider />

          {/* CONSENSUS */}
          <section id="consensus" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="BFT Protocol" title="Weighted BFT Consensus"
              subtitle="HotStuff-style BFT where a validator's turn to propose is proportional to what it contributes, and a block is final once more than two thirds of committee weight has signed it." />

            <SubHeading>Quorum is weight, not headcount</SubHeading>
            <FormulaBlock>{`quorum reached when:  signed_weight × 3  >  total_weight × 2

Strictly greater. Two thirds exactly is not a quorum: with three
equal validators that would accept two signatures, and two nodes
can disagree with the third without either being wrong.

Whose turn it is:
  turn     = height + round
  proposer = walk the committee in weight order until turn % total
             falls inside a member's share

A validator carrying three times the weight leads three times as
often. The walk is a pure function of (height, round) over a set
every node derives identically, so no two nodes elect different
proposers for the same block.`}</FormulaBlock>

            <SubHeading>The committee is frozen for an epoch</SubHeading>
            <Prose>
              The set that decides a block is read from a snapshot taken at an earlier boundary, never from whatever a node happens to hold at that moment. Two nodes sit at different heights, so a committee derived from &quot;now&quot; differs between them, they elect different proposers, and each rejects the other&apos;s blocks. Freezing the input is how Solana&apos;s leader schedule, Filecoin&apos;s power table and Helium&apos;s epochs all avoid the same failure.
            </Prose>
            <Prose>
              A node deciding a height has necessarily applied every block beneath it, so it always holds that snapshot. That is what makes the committee a function of the height being decided rather than of how far each node has got.
            </Prose>
            <Table
              headers={["Rule", "Value", "Effect"]}
              rows={[
                ["Epoch length", "100 blocks", "Registrations take effect at the next boundary, and every node seats a newcomer at the same height"],
                ["Liveness window", "500 blocks", "A validator that produces nothing for this long is unseated; it rejoins by registering again"],
                ["Jail and slash marks", "read at the epoch boundary", "So one node noticing first cannot shrink its committee while its peers keep theirs"],
                ["Minimum stake", "none by default", "Validation is open; a seat is earned by contributing, not by buying in"],
                ["Allowlist", "off", "EGO_VALIDATOR_ALLOWLIST=1 restores it, for a private deployment"],
              ]}
            />

            <SubHeading>The genesis committee</SubHeading>
            <Prose>
              Before block one there is no chain to read a validator set from, so the first set has to come from outside consensus. Ego ships a starting list of <strong>four</strong> validators, baked into the build, carrying each member&apos;s address and Dilithium public key. Carrying the key matters: the engine identifies a validator by <code style={{ color: "#00e5b0" }}>blake3(dilithium_pubkey)</code>, which cannot be recovered from a bech32 address, so a committee that shipped addresses alone would be incomplete until every member announced itself — and a member that was offline would stop the chain from ever starting.
            </Prose>
            <CalloutBox type="info">
              <strong>Four seats, quorum of three.</strong> That tolerates one member being offline or restarting. It is also why the founding four are deliberately split across separate machines: with every seat on one machine, that machine could finalize the chain alone and any fault that only appears between machines would be invisible to consensus.
            </CalloutBox>
            <Prose>
              The starting list is a bootstrap and nothing more. It buys exactly two things: a seat at block one without registering on-chain first, and exemption from the contribution floor for <strong>10,000 blocks</strong>. After that the file confers nothing — the founding members register and qualify on the same terms as everyone else, or they lose their seats. Nothing in the code treats them as special thereafter.
            </Prose>

            <SubHeading>How others join</SubHeading>
            <Prose>
              Anyone running the software registers on-chain and takes a seat at the next epoch boundary. No approval, no allowlist, no minimum stake. The founding share dilutes by arithmetic as others arrive, with no vote and no fork required:
            </Prose>
            <Table
              headers={["Committee size", "Founders' share", "Can they block a decision?"]}
              rows={[
                ["4", "100%", "yes"],
                ["10", "40%", "yes"],
                ["13", "31%", "no — below one third"],
                ["100", "4%", "no"],
              ]}
            />
            <Prose>
              Leaving is equally undramatic. A validator that goes quiet is unseated after 500 blocks, and because a departing member&apos;s weight still counts toward the total until it is jailed, a group holding under a third of the weight can leave without interrupting anything. Founders leaving one at a time is always safe; all of them leaving at once is the only pattern that pauses the chain, and only until the liveness sweep removes them.
            </Prose>

            <SubHeading>Blocks carry their author</SubHeading>
            <Prose>
              Every block is signed by the node that produced it, with the producer&apos;s Ed25519 key and a signature over the block hash. Without it the <code style={{ color: "#00e5b0" }}>miner</code> field is only a claim, and a block arriving over sync carries no proof of who built it — anyone could put another validator&apos;s name on their own block. The address the key derives to must match the miner named in the block.
            </Prose>
          </section>

          <Divider />

          {/* NETWORKING */}
          <section id="networking" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="P2P Layer" title="Networking & Cellular Safety"
              subtitle="libp2p over QUIC/UDP. Kademlia DHT peer discovery. GossipSub v1.2 per-shard topics. Cellular-safe defaults." />
            <FormulaBlock>{`// Per-shard GossipSub topics
"ego/{shard_id}/tx"        // pending transactions
"ego/{shard_id}/headers"   // block proposals (Dilithium-2 signed)
"ego/{shard_id}/consensus" // BFT votes + QCs
"ego/{shard_id}/proofs"    // PoSt/PoRep events
"ego/global/finality"      // epoch finality commits
"ego/global/rollup"        // L2 rollup commits

// Admission: peers must present a valid Dilithium-2 device certificate
// Cellular-safe: large uploads routed to Wi-Fi/Ethernet
//                control messages use cellular
//                monthly cap enforcement built-in`}</FormulaBlock>
          </section>

          <Divider />

          {/* TOKENOMICS */}
          <section id="tokenomics" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Economics" title="Tokenomics & Emissions"
              subtitle="1,000,000,000 EGOC maximum supply, emitted over 120 years, with an EIP-1559-style base-fee burn and USD-denominated reward rates." />
            <Table
              headers={["Parameter", "Value"]}
              rows={[
                ["Max supply", "1,000,000,000 EGOC"],
                ["Smallest unit", "1 uEGOC (1 EGOC = 1,000,000 uEGOC)"],
                ["Block time", "~0.1s micro-slots"],
                ["Halving interval", "Every ~4 years (1,261,440,000 blocks)"],
                ["Emission span", "30 eras across 120 years"],
                ["Node rewards", "300,000,000 EGOC (30%) — storage, coverage, retrieval, consensus"],
                ["Block emissions", "210,000,000 EGOC (21%) — released per block"],
                ["Ecosystem", "200,000,000 EGOC (20%) — pre-sale, grants, liquidity, partnerships"],
                ["Foundation", "150,000,000 EGOC (15%) — team and long-term development"],
                ["Staking rewards", "140,000,000 EGOC (14%) — paid to stakers"],
              ]}
            />
            <SubHeading>Reward Rates</SubHeading>
            <Prose>
              Node rewards are denominated in <strong style={{ color: "#c9d4e3" }}>USD</strong> and converted to EGOC at the live oracle price, so operator income stays stable whether EGOC trades at $0.01 or $10. Each payout is clamped between a floor and a ceiling, so a price collapse cannot drain the pool.
            </Prose>
            <Table
              headers={["Activity", "Rate (USD)", "Requirement"]}
              rows={[
                ["Storage hosting", "$0.002 / GB / day", "Serve files with min 2 replicas"],
                ["Consensus validation", "$0.20 / node / day", "Registered validator, staked, online"],
                ["Coverage (PoC)", "$0.15 / node / day", "Verified wireless signal at your location"],
                ["File retrieval", "$0.003 / GB served", "Active file serving"],
                ["Testnet faucet", "Testnet only", "Excluded from mainnet genesis"],
              ]}
            />
            <SubHeading>Staking</SubHeading>
            <Table
              headers={["Parameter", "Value"]}
              rows={[
                ["Base staking APR", "12.5% (DAO-tunable)"],
                ["Lock bonus", "+2% at 90d · +5% at 180d · +10% at 365d"],
                ["Reward source", "140,000,000 EGOC staking pool"],
                ["Early unstake penalty", "10% of staked amount"],
                ["Validator stake", "A validator's own stake gates its vote — the Sybil quorum gate"],
                ["Fee share", "None — transaction fees fund contributors, not stakers"],
              ]}
            />

            <SubHeading>Block Reward Schedule</SubHeading>
            <Table
              headers={["Epoch", "Block Reward", "Notes"]}
              rows={[
                ["Era 0 (years 0-4)", "0.08322 EGOC/block", "~105,000,000 EGOC - half the pool"],
                ["Era 1 (years 4-8)", "0.04161 EGOC/block", "First halving"],
                ["Era 2 (years 8-12)", "0.02081 EGOC/block", "Second halving"],
                ["Eras 3-29", "Halves every ~4 years", "210,000,000 EGOC total across 120 years"],
              ]}
            />

            <SubHeading>Fee Market (EGO-51)</SubHeading>
            <Prose>
              EIP-1559-style dynamic fees, in two parts. The <strong style={{ color: "#c9d4e3" }}>base fee</strong> adjusts per block with utilisation and is <strong style={{ color: "#c9d4e3" }}>100% burned</strong> — permanently reducing supply, which makes net issuance deflationary as usage grows. The remaining transaction fee is split <strong style={{ color: "#c9d4e3" }}>60% to the block producer</strong> and <strong style={{ color: "#c9d4e3" }}>40% to the node rewards pool</strong>, so fee revenue extends the runway for storage, coverage and retrieval payouts rather than accruing to passive holders. Wallet-to-wallet EGOC transfers have a 0 base fee floor (effectively feeless for simple transfers).
            </Prose>
          </section>

          <Divider />

          {/* TRANSACTIONS */}
          <section id="transactions" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Execution" title="Transactions & Feeless UX"
              subtitle="Resource Units replace gas fees. Declare a compute budget upfront — no per-operation charges, no unpredictable fee spikes." />
            <FormulaBlock>{`Transaction {
  from:      Address       // Dilithium-2 sender address
  to:        Address       // recipient or contract
  value:     u64           // uEGOC to transfer
  ru_limit:  u32           // hard cap on Resource Units consumed
  payload:   Bytes         // contract call data (empty for transfers)
  nonce:     u64           // replay protection
  deadline:  u64           // invalid after this block height
  signature: DilithiumSig  // Dilithium-2 signature
}`}</FormulaBlock>
            <CalloutBox type="success">
              TXs submitted to the 16-shard mempool are confirmed within the next 50 ms batch window — up to 2,000 TXs packed into one block per shard with a single disk write.
            </CalloutBox>
          </section>

          <Divider />

          {/* PRIVACY */}
          <section id="privacy" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Security" title="Shielded Transactions" color="#fbbf24"
              subtitle="A shielded pool proved with hash-based STARKs. The chain records that someone withdrew, without recording who deposited. No trusted setup." />
            <Prose>
              On most blockchains every transfer names both parties, so anyone can profile a wallet, follow a large holder, or target an individual by wealth. Ego's shielded pool breaks that link cryptographically rather than hiding it in the interface.
            </Prose>
            <Prose>
              You move funds into the pool with a <strong>shield</strong> deposit. Your wallet keeps a secret note. Later you <strong>withdraw</strong> to any address. The withdrawal's sender field is the pool's own address, identical on every withdrawal, and it carries a zero-knowledge proof instead of a signature. Nothing on the chain connects it to the deposit that funded it.
            </Prose>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px,1fr))", gap: 14, margin: "28px 0" }}>
              <InfoCard icon="🛡️" title="No sender on chain" accent="#fbbf24">
                A withdrawal's <code>from</code> is the pool address, the same for everyone. Your address appears on your deposit and nowhere else.
              </InfoCard>
              <InfoCard icon="🚫" title="No trusted setup" accent="#fbbf24">
                STARKs are hash-based, so there is no ceremony and no toxic waste. A pairing-based SNARK would need both, and would not be post-quantum.
              </InfoCard>
              <InfoCard icon="🔑" title="Nullifiers stop double spends" accent="#fbbf24">
                Spending publishes a one-way fingerprint of the note. The chain refuses a repeat, yet the fingerprint reveals nothing about which deposit it came from.
              </InfoCard>
              <InfoCard icon="🪙" title="Fixed denominations" accent="#fbbf24">
                Notes come in 1, 10, 100, 1,000 and 10,000 EGOC so every note looks alike. Amounts must be public for the ledger to balance, and identical sizes are what stop the amount identifying you.
              </InfoCard>
              <InfoCard icon="🧮" title="Cost" accent="#fbbf24">
                A withdrawal proof is about 22 KB and verifies in roughly 4 ms. One withdrawal spends up to 16 notes, so an arbitrary total is one transaction rather than a run of them.
              </InfoCard>
              <InfoCard icon="⚠️" title="Not yet audited" accent="#fbbf24">
                The pool runs on testnet and has had no external review. Treat it as experimental until it has been audited.
              </InfoCard>
            </div>

            <SubHeading>What the chain records</SubHeading>
            <Table
              headers={["Field", "Ordinary transfer", "Shielded withdrawal"]}
              rows={[
                ["From address", "egot1… (the sender)", "the pool address, identical for all"],
                ["To address", "egot1… (visible)", "egot1… (visible)"],
                ["Amount", "visible", "visible, and always a fixed denomination"],
                ["Link to the sender", "explicit", "none — a STARK proof stands in for a signature"],
                ["Double-spend guard", "nonce", "nullifier set"],
              ]}
            />

            <SubHeading>What the status is telling you</SubHeading>
            <Prose>
              Every note in the wallet carries a status, and they are not interchangeable. The two that matter most are the ones about money: whether it has really arrived, and whether it can be lost.
            </Prose>
            <Table
              headers={["Status", "What it means", "Can you spend it?"]}
              rows={[
                ["pending", "Submitted, not in a block yet", "No"],
                ["ready", "In the commitment tree", "Yes"],
                ["spending", "Withdrawal sent, not in a block yet", "No"],
                ["settling", "In a block this node holds, not yet finalized", "No — the note is gone"],
                ["spent", "In a finalized block", "No — genuinely delivered"],
                ["cancelled", "Cancelled before it landed", "No"],
                ["returned", "Never landed and never will", "The coins were never taken"],
              ]}
            />
            <CalloutBox type="info">
              <strong>Delivered means the committee agreed, not that your node holds a block.</strong> A note only reads <em>spent</em> once its block is at or below the finalized height. A block below that can still be reorganised away, and showing you that coins arrived while the recipient&apos;s node has never seen them is the worst mistake this screen could make — you would believe you had paid when you had not.
            </CalloutBox>
            <Prose>
              The other half of that promise runs the opposite way. <strong>Coins are never lost to a transaction that did not execute.</strong> A deposit that never reaches a block ends as <em>returned</em> with the balance untouched, because the transfer never happened — there is nothing to recover, it was never taken. A withdrawal that never lands puts the note back to <em>ready</em>, and it can be spent again. Changing network in the middle of either is not a way to lose money.
            </Prose>
            <Prose>
              None of these are decided while the node is behind the network. A node still catching up cannot tell &quot;never included&quot; from &quot;in a block I have not read yet&quot;, so it waits rather than guessing, in both directions.
            </Prose>

            <SubHeading>Taking one back</SubHeading>
            <Prose>
              A deposit or a withdrawal that has not reached a block yet can be cancelled from the wallet. A transaction that never executes never moved anything, so the coins simply stay where they were.
            </Prose>
            <Prose>
              Cancelling a deposit does <strong>not</strong> destroy the note&apos;s secret. That is the one mistake here which cannot be undone: a deposit already travelling may land anyway, and without the secret those coins would sit in the pool with nothing able to spend them. The note is kept and marked instead, so a late arrival shows up in the commitment tree and becomes spendable again.
            </Prose>
            <CalloutBox type="info">
              Neither cancel will act while the node is behind the network. A node that has not applied the block carrying your transaction cannot tell &quot;not included&quot; from &quot;not seen yet&quot;, and guessing would release notes the chain has already spent — you would watch the coins come back, send them again, and be told they were gone.
            </CalloutBox>

            <SubHeading>Keeping the pool honest</SubHeading>
            <Prose>
              The pool&apos;s recorded total and the balance held at its address are two views of the same coins, and every node checks them against each other as blocks apply. If they disagree the node says so and rebuilds the pool from the chain.
            </Prose>
            <Prose>
              That rebuild refuses rather than guesses. Replaying a history a node cannot read in full — because it fast-synced from a state snapshot, because blocks were pruned, or because a block lists fewer transactions than it claims to carry — would find the deposits and miss what spent them, and the pool would report holding far more than it does. A node that cannot verify its own history asks a peer for the pool state instead, which travels inside a state snapshot.
            </Prose>
            <Table
              headers={["Setting", "Value", "Meaning"]}
              rows={[
                ["Denominations", "1, 10, 100, 1,000, 10,000 EGOC", "Every note of a size looks alike"],
                ["Root history", "100 roots", "A withdrawal proves against a recent tree root, not only the current one"],
                ["Fee on shielding", "per note, on top of the amount", "Four notes cost four fees"],
                ["Fee on withdrawing", "once, out of the amount", "The recipient receives that much less"],
                ["Where fees go", "burned", "No validator collects them"],
              ]}
            />

            <SubHeading>What it does not hide</SubHeading>
            <Prose>
              Be clear about the limits. Deposits and withdrawals are both real transfers, so the <strong>amounts are public</strong> — they have to be, or nodes could not check that the pool balances. What is hidden is which deposit paid for which withdrawal.
            </Prose>
            <Prose>
              That protection comes from the crowd. Your anonymity set is everyone else holding notes of the size you are spending, so a pool with few users offers little cover, and withdrawing moments after depositing can still be linked by timing. Large holders are the most exposed, because few others hold the biggest notes. Waiting, splitting across addresses and dates, and using denominations other people also hold all help. <strong>Shielded-to-shielded transfers</strong>, which would hide amounts too and remove the need for denominations, are not implemented yet.
            </Prose>
          </section>

          <Divider />

          {/* STORAGE DEALS */}
          <section id="storage" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Storage Layer" title="Storage Deals & Data Lifecycle"
              subtitle="On-chain enforced deals connect clients with operators. PoRep + PoSt ensures data is stored continuously and retrievable on demand." />
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Deal Proposal">Client proposes: CID, size, duration, price/byte-month, min replication factor.</StepCard>
              <StepCard number="2" title="Operator Acceptance">Operator locks collateral (slashed on PoSt failures). Signed with Dilithium-2.</StepCard>
              <StepCard number="3" title="Sealing (PoRep)">Operator seals data → CommD, CommR, ZK proof → PoRepEvent anchored on-chain.</StepCard>
              <StepCard number="4" title="Proving (PoSt)">Each epoch: pass WindowPoSt or earn zero storage rewards. 3 misses → slashing + repair job.</StepCard>
              <StepCard number="5" title="Retrieval">Client requests by CID. Operator serves chunks with Merkle inclusion proofs against CommR.</StepCard>
              <StepCard number="6" title="Expiry / Repair">On expiry, sector freed. Mid-deal dropout → repair job restores RF=3 with new operators.</StepCard>
            </div>
          </section>

          <Divider />

          {/* COMPUTE */}
          <section id="compute" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Compute Layer" title="DePIN Compute" color="#8b5cf6"
              subtitle="Rent idle GPU and CPU hardware from independent providers worldwide, or list your own. All payments flow through on-chain escrow — trustless, automatic, and refundable." />
            <Prose>
              Ego's compute marketplace connects renters who need GPU/CPU power with providers who have spare capacity. Payments are locked in EGOC escrow at booking time and released automatically each period as the provider heartbeats. If a provider goes offline, unused escrow is refunded to the renter. No third party, no billing account — just on-chain EGOC.
            </Prose>

            <SubHeading>Single GPU Rental — Rent Tab</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Browse the Marketplace">Open the Compute tab → Rent. Every GPU and CPU listed by active providers appears in real time via libp2p gossip. Filter by workload type: LLM, Image Gen, Fine-tune, Embeddings.</StepCard>
              <StepCard number="2" title="Select Duration & Book">Choose how long you need (30 minutes to 1 year). The total EGOC cost is shown upfront. Confirming locks the full amount into on-chain escrow immediately.</StepCard>
              <StepCard number="3" title="Open AI Workspace">Once booked and active, click Open AI Workspace. Your browser-based workspace launches — all compute runs on the remote GPU, your computer is just the screen.</StepCard>
              <StepCard number="4" title="Payments Release Per Period">Escrow releases to the provider automatically each period (e.g. every hour). If the provider misses a heartbeat, the remaining escrow is refunded to you.</StepCard>
              <StepCard number="5" title="Terminate Anytime">Early termination costs one period as a penalty. All remaining escrow beyond that is refunded immediately to your wallet.</StepCard>
            </div>

            <SubHeading>AI Workspace — Browser-Based Apps on Remote GPU</SubHeading>
            <Prose>
              The AI Workspace is a unified control panel for all your active rentals. You do not choose which GPU to open — one workspace shows everything. A tab bar at the top lets you switch between nodes. The header always shows your combined total CPU cores, RAM, and GPU count across all active rentals.
            </Prose>
            <div className="docs-grid-2" style={{ marginBottom: 24 }}>
              <InfoCard icon="💬" title="LLM Chat" accent="#8b5cf6">Run any open-source language model (Mistral, LLaMA, Phi, Gemma) on the remote GPU. Gradio-based chat UI opens in your browser automatically. The provider installs packages on first launch; subsequent launches are instant.</InfoCard>
              <InfoCard icon="📓" title="JupyterLab" accent="#8b5cf6">Full Python notebook environment on the remote GPU. All packages, CUDA, and the filesystem of the remote node are available. Your local browser connects via a libp2p-tunnelled port.</InfoCard>
              <InfoCard icon="🎨" title="Image Generation" accent="#8b5cf6">Stable Diffusion and SDXL via automatic1111 or a lightweight Gradio wrapper. Generates images on the remote GPU; results stream back to your browser tab.</InfoCard>
              <InfoCard icon="🎤" title="Transcribe Audio" accent="#8b5cf6">OpenAI Whisper running on the remote GPU. Upload an audio file (MP3, WAV, M4A, OGG, FLAC, WebM) from your computer; the transcript is returned in seconds. Pick your Whisper model size in the workspace.</InfoCard>
            </div>

            <SubHeading>Multi-Rental: Combining Compute</SubHeading>
            <Prose>
              If you rent from multiple providers simultaneously, all rentals are managed inside a single AI Workspace. The stats bar shows your total pooled resources — e.g. two rentals with 4 cores / 2 GB and 4 cores / 5 GB combine to show 8 cores / 7 GB total. Switch between individual nodes using the tab bar inside the workspace to view per-node live CPU, RAM, and GPU utilisation.
            </Prose>
            <CalloutBox type="success">
              You never need to choose which rental to open before entering the workspace. All active rentals are accessible from a single "Open AI Workspace" button. Multi-node workflows (e.g. running LLM inference on one node while transcribing audio on another) are fully supported.
            </CalloutBox>

            <SubHeading>File Transfer</SubHeading>
            <Prose>
              Files can be sent to and from any rented node directly inside the workspace. Use <strong style={{ color: "#c9d4e3" }}>Add File</strong> to upload a single file or <strong style={{ color: "#c9d4e3" }}>Add Folder</strong> to upload an entire directory. Files land in the remote node's working directory and are immediately accessible to any running app. Download files back to your local machine at any time.
            </Prose>

            <SubHeading>Isolation — Docker Sandbox</SubHeading>
            <div className="docs-grid-2" style={{ marginBottom: 24 }}>
              <InfoCard icon="🔒" title="Docker Isolated (default)" accent="#8b5cf6">When Docker is installed on the provider node, each renter gets an isolated container. The host filesystem and other renters' workloads are invisible. Ports are mapped per-container; the provider never exposes the raw process to other users.</InfoCard>
              <InfoCard icon="⛔" title="No Docker, no rentals" accent="#f59e0b">A renter's command never runs on the provider's own system. A machine can only list capacity while Docker is running, and if Docker stops, the provider refuses commands and file transfers instead of falling back to the host. The provider also checks every rental against an offer it listed itself, and only the renter's key can use it.</InfoCard>
            </div>

            <SubHeading>For Providers — Earn Tab</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="List Your Hardware">Go to Compute → Earn → List Capacity. Set your CPU cores, RAM, GPU model and VRAM, and price per hour. The offer broadcasts to the network via libp2p gossip immediately.</StepCard>
              <StepCard number="2" title="Wait for Bookings">Buyers browse the marketplace and book your hardware. Once a booking is confirmed, your hardware shows as reserved and the escrow is locked.</StepCard>
              <StepCard number="3" title="Auto-Heartbeat & Payment">Every 30 seconds, Ego Desktop checks if a payment period has elapsed. If so, it automatically sends a heartbeat and claims the period payment from escrow to your wallet — no manual action required.</StepCard>
              <StepCard number="4" title="Refund on Failure">If you go offline and miss a heartbeat window, the corresponding period payment is refunded to the renter. Your listing remains active for future bookings once you come back online.</StepCard>
            </div>

            <SubHeading>Escrow Mechanics</SubHeading>
            <Table
              headers={["Event", "What Happens"]}
              rows={[
                ["Booking confirmed", "Full EGOC cost locked in on-chain escrow immediately"],
                ["Provider heartbeat (each period)", "One period's payment released from escrow → provider wallet"],
                ["Provider misses heartbeat", "That period's payment refunded to renter; miss counter increments"],
                ["Renter terminates early (no breach)", "1-period penalty deducted; all remaining escrow refunded to renter"],
                ["Renter terminates (provider breach ≥1)", "Renter gets full refund + provider's security deposit (collateral)"],
                ["Duration expires naturally", "All remaining escrow refunded to renter; rental ends"],
              ]}
            />

            <SubHeading>GPU Clusters — Train Tab</SubHeading>
            <Prose>
              The Train tab is designed for enterprise-scale AI training workloads. Instead of one GPU, you book from 2 to 200 independent providers simultaneously. All nodes auto-join a private WireGuard VPN mesh, giving you a single cluster with one head-node IP — ready for distributed PyTorch, DeepSpeed, or any Ray-compatible framework.
            </Prose>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Configure Your Cluster">Set the number of GPUs, minimum VRAM per GPU, CPU cores, RAM, duration, and framework (Ray or raw SSH). Name your cluster for easy reference.</StepCard>
              <StepCard number="2" title="Providers Auto-Join">When the cluster booking broadcasts via libp2p, qualifying providers auto-join. Each generates a WireGuard keypair, registers their external IP and public key, and receives their VPN IP in the cluster subnet.</StepCard>
              <StepCard number="3" title="WireGuard Mesh Active">Once enough nodes have joined, the cluster goes Active. Each node has a WireGuard IP in a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>10.X.Y.0/24</code> subnet derived from the cluster ID. The buyer gets IP <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>10.X.Y.254</code>.</StepCard>
              <StepCard number="4" title="Connect & Train">Download your WireGuard config from the cluster card. On Windows: WireGuard → Import tunnel from file → Activate. On Linux/macOS: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>wg-quick up wg0</code>. Then connect to the head node via Ray or SSH.</StepCard>
              <StepCard number="5" title="Payments & History">Each provider node claims payment per period via auto-heartbeat. Terminated clusters remain visible in a history section so you can review past jobs and costs.</StepCard>
            </div>

            <SubHeading>Cluster WireGuard Mesh — Technical Details</SubHeading>
            <FormulaBlock>{`// Subnet derived deterministically from cluster_id
subnet = blake3(cluster_id)[0..2]   → 10.{subnet[0]}.{subnet[1]}.0/24

// Node WireGuard IPs assigned in order:
node_0 (head)  → 10.X.Y.1
node_1         → 10.X.Y.2
...
node_N         → 10.X.Y.N+1
buyer          → 10.X.Y.254         // buyer joins the mesh as a peer

// Each provider node:
// 1. Generates X25519 WireGuard keypair at auto_join_cluster time
// 2. Looks up external public IP via api4.my-ip.io (fallback: LAN IP)
// 3. Registers endpoint: {public_ip}:{WG_PORT} + wg_pubkey via gossip
// 4. Applies WireGuard config with all other nodes as Peers
// 5. Optionally starts Ray:  ray start --head  (head)
//                             ray start --address={head_wg_ip}:6379  (worker)`}</FormulaBlock>

            <Table
              headers={["Parameter", "Value"]}
              rows={[
                ["Min cluster size", "2 provider nodes"],
                ["Max cluster size", "200 provider nodes"],
                ["VPN technology", "WireGuard (X25519 keypairs)"],
                ["Subnet allocation", "10.X.Y.0/24 deterministic from cluster_id (BLAKE3)"],
                ["Frameworks supported", "Ray (auto-started), SSH (manual)"],
                ["Escrow", "Total cluster cost locked at booking; per-node per-period release"],
                ["Heartbeat interval", "Every 30 seconds (auto, no user action required)"],
                ["Terminated clusters", "Visible in history — not deleted"],
              ]}
            />

            <CalloutBox type="info">
              Clusters are for corporate and research workloads requiring multi-GPU scale. Individual renters who need one or two GPUs should use the Rent tab instead — simpler setup, same escrow guarantees.
            </CalloutBox>
          </section>

          <Divider />

          {/* NODES */}
          <section id="nodes" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Join the Network" title="Node Types & How to Join" color="#0066ff"
              subtitle="Anyone can run an ego-node today. Full hardware-attested validator status rolls out with Ego Devices." />

            <SubHeading>Run a Node Now</SubHeading>
            <FormulaBlock>{`git clone https://github.com/ego-blockchain/ego-blockchain-
cd ego-blockchain
cargo build -p ego-node --release

# Full node (P2P port 9000, HTTP RPC port 8545)
./target/release/ego-node --type full --port 9000

# Connect to a bootstrap peer
./target/release/ego-node --type full --bootstrap /ip4/1.2.3.4/tcp/9000/p2p/12D3Koo...

# Storage-only node (500 GB)
./target/release/ego-node --type storage --storage 500

# Validator node (requires staked EGOC)
./target/release/ego-node --type validator --shards 0,1,2`}</FormulaBlock>

            <SubHeading>HTTP RPC (port 8545)</SubHeading>
            <Table
              headers={["Method", "Endpoint", "Description"]}
              rows={[
                ["GET", "/health", "Node status, block height, peer ID"],
                ["GET", "/chain/blocks", "Last 50 block summaries"],
                ["GET", "/block/:height", "Single block by height"],
                ["GET", "/balance/:address", "EGOC balance for a 20-byte hex address"],
                ["POST", "/tx/submit", "Submit a signed transaction"],
                ["GET", "/chain/transactions", "Last 50 pending transactions"],
                ["GET", "/node/stats", "Node performance metrics"],
                ["POST", "/faucet", "Testnet faucet — request test EGOC (body: {address})"],
              ]}
            />

            <SubHeading>Node Types</SubHeading>
            <div style={{ }} className="docs-grid-2">
              <InfoCard icon="🗳️" title="Validator">BFT consensus. Casts Dilithium-2 votes, forms QCs. Requires staked EGOC + uptime.<div style={{ marginTop: 8 }}><Tag color="#ec4899">Earns: Consensus bucket (25%)</Tag></div></InfoCard>
              <InfoCard icon="💾" title="Storage Provider">Seals sectors, passes WindowPoSt, serves retrieval. Requires locked collateral.<div style={{ marginTop: 8 }}><Tag color="#00e5b0">Earns: Storage bucket (55%)</Tag></div></InfoCard>
              <InfoCard icon="📡" title="Beacon / Witness">Records 5G RF metrics, submits PoC reports. Requires Ego-certified hardware.<div style={{ marginTop: 8 }}><Tag color="#0066ff">Earns: Coverage bucket (20%)</Tag></div></InfoCard>
              <InfoCard icon="💡" title="Light Client">Verifies headers, QCs, state proofs. No staking. Used by wallets and dApps.<div style={{ marginTop: 8 }}><Tag>Read-only, no staking</Tag></div></InfoCard>
            </div>
          </section>

          <Divider />

          {/* EGO DEVICES */}
          <section id="ego-devices" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Hardware — Coming Soon" title="Ego Devices" color="#ec4899"
              subtitle="Purpose-built hardware that makes running a full Ego node as simple as plugging in a router — unlocking storage rewards, PoC proofs, and hardware-attested validator status." />

            <Prose>
              Software nodes are the first step. The full vision requires physical devices that passively measure 5G coverage, store data in sealed sectors, and run with non-exportable TPM-backed keys. Ego Devices are engineered to make this plug-and-play: connect to Wi-Fi, earn EGOC.
            </Prose>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px,1fr))", gap: 14, margin: "28px 0" }}>
              <InfoCard icon="📡" title="5G Modem (Sub-6 + mmWave)" accent="#ec4899">Passive downlink scanner. Records RSRP, RSRQ, SINR, PCI, NR-ARFCN. Signed WitnessReports. Never transmits on licensed spectrum.</InfoCard>
              <InfoCard icon="💾" title="NVMe Storage Array" accent="#00e5b0">Dedicated storage for sealed sectors. PoRep sealing runs on-device. WindowPoSt proofs generated locally.</InfoCard>
              <InfoCard icon="🔐" title="TPM / Secure Enclave" accent="#0066ff">Non-exportable Dilithium-2 keys generated at manufacture. Hardware attestation at every P2P handshake. One device = one identity.</InfoCard>
              <InfoCard icon="📍" title="GNSS Receiver" accent="#f59e0b">Precise location binding for PoC events. Cryptographically bound to challenge window nonces — position cannot be spoofed without the TPM key.</InfoCard>
              <InfoCard icon="⚡" title="Low-Power Compute" accent="#8b5cf6">ARM-based SoC. Fanless. Target: under 15W at full load (validation + storage + coverage scanning).</InfoCard>
              <InfoCard icon="🌐" title="Multi-WAN" accent="#ec4899">Gigabit Ethernet + Wi-Fi 6E + optional 5G SIM. Intelligent interface selection — heavy proofs over Ethernet, control over cellular. Monthly cap enforcement built-in.</InfoCard>
            </div>

            <SubHeading>What Devices Unlock vs Software Nodes</SubHeading>
            <Table
              headers={["Feature", "Software Node", "Ego Device"]}
              rows={[
                ["Validator rewards (consensus)", "✅ With staked EGOC", "✅ Hardware-attested"],
                ["Storage rewards (PoSt/PoRep)", "⚠️ Partial", "✅ Full sealing + proving"],
                ["Coverage rewards (PoC)", "❌ Requires 5G modem", "✅ Built-in 5G scanner"],
                ["Hardware attestation", "❌", "✅ TPM/SE Dilithium-2 keys"],
                ["DRS multiplier max", "0.9×", "1.3× (high PoC quality)"],
                ["Sybil resistance", "Software-only", "TPM-bound (strong)"],
                ["Setup complexity", "CLI + config", "Plug in + mobile app"],
              ]}
            />

            <CalloutBox type="warning">
              Ego Devices are in active development. Register interest at <strong style={{ color: "#c9d4e3" }}>egoblockchain.com</strong> to be notified when pre-orders open. Early registered nodes receive a DRS bonus multiplier at mainnet launch.
            </CalloutBox>

            <SubHeading>Device Certification</SubHeading>
            <FormulaBlock>{`// Certificate chain (burned to TPM at manufacture)
Ego Hardware Root CA  (SPHINCS+ / SLH-DSA, offline)
  └── Ego Device CA  (Dilithium-2, online)
         └── Per-Device Cert  (Dilithium-2, TPM-bound)
                ├── device_id  (20-byte address)
                ├── model_id   (certified hardware model)
                └── signed by Device CA

// At P2P handshake:
// Peer presents per-device cert → verified against Device CA → Root CA
// TPM attests current key bound to device_id (export impossible)`}</FormulaBlock>
          </section>

          <Divider />

          {/* GOVERNANCE */}
          <section id="governance" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="DAO" title="Governance — Fully Decentralized"
              subtitle="Two-type voting: stake power + knowledge power. Community-submitted proposals and tests. No admin override, no founding-team veto." />

            <Prose>
              The Ego DAO is the sole mechanism for changing protocol parameters, enabling features, and allocating treasury funds. Everything is community-submitted — proposals, knowledge tests, and votes. The founding team has no special veto or override power.
            </Prose>

            <SubHeading>Two-Type Voting</SubHeading>
            <FormulaBlock>{`// Stake Vote — economic alignment
stake_power     = your_staked_egoc / total_staked_egoc

// Knowledge Vote — informed participation
// Voter takes an attached multiple-choice test (1–10 questions)
// Score = correct_answers / total_questions × 10 knowledge points
knowledge_power = score / max_score × 10

// Combined (both required to reach quorum)
combined_power  = (stake_power + knowledge_power) / 2`}</FormulaBlock>

            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="💰" title="Stake Vote">
                Reflects economic commitment. Power = your staked EGOC ÷ total staked network-wide. Prevents pure plutocracy — stake vote alone is not enough.
              </InfoCard>
              <InfoCard icon="🧠" title="Knowledge Vote">
                Voter answers a multiple-choice test attached to the proposal. Score proportional to correct answers × 10 knowledge points. Rewards informed participation over capital concentration.
              </InfoCard>
            </div>

            <SubHeading>Proposal Types</SubHeading>
            <Table
              headers={["Type", "Purpose", "Example"]}
              rows={[
                ["ParameterChange", "Tune a numeric protocol parameter", "Change staking APR from 20% → 22%"],
                ["FeatureFlag", "Enable or disable a protocol feature", "Activate ZK rollups on mainnet"],
                ["TextResolution", "Non-binding DAO signal or statement", "Community position on bridge fee"],
              ]}
            />

            <SubHeading>Proposal Lifecycle</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Create Proposal">Any community member creates a proposal: title, description, type, options, duration (default 7 days). Optionally attach a knowledge test (1–10 questions with correct answers stored server-side).</StepCard>
              <StepCard number="2" title="Voting Period">Both stake vote and knowledge vote are open. Voters pick an option for stake vote. For knowledge vote: answer the test → preview your score → submit vote. Both votes required.</StepCard>
              <StepCard number="3" title="Quorum Check">5% minimum quorum required (combined_power across all voters). Proposals with insufficient participation do not pass regardless of vote distribution.</StepCard>
              <StepCard number="4" title="Result">Winning option needs &gt;50% combined_power. Results show per-option stake_power, knowledge_power, and combined_power. Stored on-chain in RocksDB CF_DAO.</StepCard>
            </div>

            <SubHeading>Knowledge Test Format</SubHeading>
            <Prose>
              Tests have 1–10 questions, each with 2–4 multiple-choice options. The proposal creator marks correct answers at creation time. Correct answers are stored server-side and not revealed to voters until after the voting period ends — preventing gaming.
            </Prose>

            <SubHeading>DAO-Tunable Parameters</SubHeading>
            <Table
              headers={["Parameter", "Default", "Notes"]}
              rows={[
                ["Reward split", "Storage 55% / Consensus 25% / Coverage 20%", "Per block reward distribution"],
                ["Staking APR", "12.5% base + lock bonuses", "Tunable via ParameterChange proposal"],
                ["Market tax split", "50% staking pool / 50% treasury", "1% buy/sell tax on AMM/CEX trades"],
                ["DRS weights", "0.40 / 0.20 / 0.20 / 0.10 / 0.10", "PoSt / PoC / uptime / serve / latency"],
                ["PoC H3 resolution", "r=8 urban, r=7 rural", ""],
                ["PoSt SLA window", "3–10 minutes", ""],
                ["Consecutive miss threshold", "3 windows → slashing", ""],
                ["DRS multiplier range", "[0.7, 1.3]", ""],
                ["Voting period", "7 days (default)", "Proposal creator can set custom duration"],
                ["Quorum", "5% combined power", ""],
              ]}
            />
          </section>

          <Divider />

          {/* SECURITY */}
          <section id="security" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Security" title="Security Model & Threat Analysis"
              subtitle="BFT safety, hardware attestation, anti-Sybil proofs, and post-quantum crypto from day one." />
            <div style={{ }} className="docs-grid-2">
              <InfoCard icon="🛡️" title="Byzantine Fault Tolerance">f &lt; n/3 malicious validators tolerated per shard. 2f+1 Dilithium-2 votes required for a valid QC.</InfoCard>
              <InfoCard icon="🚫" title="Equivocation Slashing">Double-signing detected via conflict proofs. Immediate stake slashing on evidence. Verifiable by light clients.</InfoCard>
              <InfoCard icon="📻" title="SDR Replay Attack">Co-beacon carries a time-bound signed nonce. Replay requires the device TPM private key — physically impossible to export.</InfoCard>
              <InfoCard icon="🤝" title="Sybil Witness Collusion">Min 3 witnesses from ≥2 H3 cells, ≥2 distinct wallets, no shared IP or on-chain history.</InfoCard>
              <InfoCard icon="💾" title="Sybil Storage">PoRep replica_id encodes node identity. Two nodes sealing the same data produce cryptographically distinct replicas.</InfoCard>
              <InfoCard icon="🔮" title="Quantum Threat">All signatures: Dilithium-2 (FIPS 204). All key exchange: ML-KEM (FIPS 203). Harvest-now-decrypt-later surface eliminated from day one.</InfoCard>
            </div>
          </section>

          <Divider />

          {/* EIPs */}
          <section id="eips" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Protocol Standards" title="Ego Improvement Proposals (EIPs)"
              color="#8b5cf6"
              subtitle="All protocol changes, token standards, and DeFi primitives on Ego Blockchain are defined through numbered EIPs — open documents tracked as GitHub issues that anyone can propose and discuss." />
            <Prose>
              EIPs are the only way to change the protocol. Each proposal goes through a public discussion on GitHub, moves from Planned → In Progress → Review → Final, and is referenced in every commit and PR that implements it. All 21 current EIPs are open on GitHub at <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego-blockchain/ego-blockchain-</code> issues #11–#33.
            </Prose>

            <SubHeading>Core Protocol (EGO-1 – EGO-6)</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="⚡" title="EGO-1 — HotStuff BFT Consensus" accent="#8b5cf6">
                Pipelined HotStuff BFT. 2f+1 quorum, 10s view-change timeout, automatic leader rotation. Blocks finalized at N-2. Minimum 1,000 EGOC stake to register as validator. Slashing burns 10% of actual stake. Max 10,000 validators.
              </InfoCard>
              <InfoCard icon="📡" title="EGO-2 — Proof of Coverage" accent="#8b5cf6">
                Rewards nodes 8 EGOC/day for provable 5G coverage. Challenge windows derived from VRF randomness. Requires 3 independent witnesses across ≥2 H3 cells. Quality score q ∈ [0.5, 1.0] based on 3GPP path-loss fitting.
              </InfoCard>
              <InfoCard icon="🔀" title="EGO-3 — Sharding" accent="#8b5cf6">
                16 parallel shards for 100k+ TPS. Address prefix routes TXs via FNV-1a hash. Shard assignments published to Kademlia DHT. Master/Slave roles with health checks. Cross-shard two-phase commit with receipt proofs.
              </InfoCard>
              <InfoCard icon="🔄" title="EGO-4 — ZK Rollups" accent="#8b5cf6">
                Off-chain TX batching with on-chain STARK/Groth16 validity proofs. Up to 10,000 TXs per proof. State root finalized after 2 epochs. Full data availability via EGO-43. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego-rollup</code> crate scaffolded.
              </InfoCard>
              <InfoCard icon="📱" title="EGO-5 — Light Client" accent="#8b5cf6">
                SPV for mobile/IoT. Download headers only — O(log N) Blake3 Merkle proofs for TX inclusion. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>get_block_headers</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>get_tx_proof</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>verify_tx_proof</code> — all exposed via JSON-RPC.
              </InfoCard>
              <InfoCard icon="🔱" title="EGO-6 — Fork Choice Rule" accent="#8b5cf6">
                Highest <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>vote_count</code> wins. Tie-break: lower block hash (lexicographic). Finalized blocks (depth ≥ 2) are never reorged. Max reorg depth: 100 blocks. Any deeper reorg triggers slashing.
              </InfoCard>
            </div>

            <SubHeading>Token Standards (EGO-20 – EGO-23)</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🪙" title="EGO-20 — Fungible Token Standard" accent="#00e5b0">
                Common interface: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>transfer</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>approve</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>transfer_from</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>balance_of</code>. Required events: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Transfer</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Approval</code>. Compatible with all DEXes, lending pools, and wallets.
              </InfoCard>
              <InfoCard icon="🖼️" title="EGO-21 — Non-Fungible Token Standard" accent="#00e5b0">
                NFTs with <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>u64</code> token IDs. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>mint</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>transfer</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>approve</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>owner_of</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>set_approval_for_all</code>. Optional <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>token_uri</code> for metadata.
              </InfoCard>
              <InfoCard icon="🔄" title="EGO-22 — Multi-Token Standard" accent="#00e5b0">
                Manage multiple token types (fungible + NFT) in a single contract. Batch transfers. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>balances: map&lt;u64, map&lt;address, u64&gt;&gt;</code>. Ideal for games, metaverse platforms, and DeFi.
              </InfoCard>
              <InfoCard icon="🔒" title="EGO-23 — Soulbound Token Standard" accent="#00e5b0">
                Non-transferable tokens for identity, credentials, and reputation. Transfer function always reverts. Issuer can revoke. Used for KYC badges, governance voting rights, on-chain certifications.
              </InfoCard>
            </div>

            <SubHeading>DeFi Standards (EGO-30 – EGO-33)</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="📈" title="EGO-30 — DEX / AMM Standard" accent="#f59e0b">
                Constant-product AMM (x×y=k). 0.3% swap fee to LPs. TWAP oracle over last 30 blocks. Flash loans with 0.09% fee. Any EGO-20 pair. LP tokens are themselves EGO-20 compliant.
              </InfoCard>
              <InfoCard icon="🏦" title="EGO-31 — Lending Protocol" accent="#f59e0b">
                Overcollateralised lending. 150% collateral ratio. 120% liquidation threshold — liquidators earn 5% bonus. Utilisation-based interest model. Whitelisted assets via EGO-52 governance.
              </InfoCard>
              <InfoCard icon="💵" title="EGO-32 — Algorithmic Stablecoin" accent="#f59e0b">
                Over-collateralised USDEGO pegged to USD. Mint by depositing EGOC at 200% ratio. 0.5% annualised stability fee. EGO-52 can freeze minting if peg deviates &gt;10% for 1 hour.
              </InfoCard>
              <InfoCard icon="🌾" title="EGO-33 — Yield Farming" accent="#f59e0b">
                EGOC emissions to whitelisted LP token pools. Allocation-point system. Rewards accumulate per block. Up to 2.5× boost for staked EGOC holders (EGO-52).
              </InfoCard>
            </div>

            <SubHeading>Infrastructure (EGO-40 – EGO-44)</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="📲" title="EGO-40 — WalletConnect" accent="#0066ff">
                WalletConnect v2-compatible. Quantum-safe session keys via ML-KEM (Kyber-768). QR pairing from Ego Desktop. Methods: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_sendTransaction</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_signMessage</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_getAccounts</code>.
              </InfoCard>
              <InfoCard icon="🔁" title="EGO-41 — EVM Compatibility" accent="#0066ff">
                Full EVM (London spec, REVM). Solidity contracts deploy unchanged. MetaMask, Hardhat, Foundry supported. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>eth_*</code> RPC at <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>/evm</code>. Chain ID 1111 for Ego mainnet.
              </InfoCard>
              <InfoCard icon="🌉" title="EGO-42 — Cross-Chain Bridge" accent="#0066ff">
                Lock-and-mint to Ethereum, BNB Chain, Solana. 5-of-9 relayer multisig. 0.1% bridge fee. 12-block finality requirement. 24h time-lock on large withdrawals (&gt;10,000 EGOC equivalent).
              </InfoCard>
              <InfoCard icon="💾" title="EGO-43 — Decentralized Storage" accent="#0066ff">
                AES-256-GCM content-addressed storage. CID: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egocid1{'{'}{'{'}blake2_hex{'}'}{'}'}</code>. 0.5 EGOC/GB/day. Min 2 replicas enforced every 30s. Share bundles: <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoshare1</code> and <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>egoshare2</code>. ✅ Implemented.
              </InfoCard>
              <InfoCard icon="📊" title="EGO-44 — Chain Indexer" accent="#0066ff">
                Real-time indexer alongside ego-node. RocksDB <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>addr_txs</code> and <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>balances</code> CFs already live. Cursor-based pagination, max 100 results/page. Event log and full API pending.
              </InfoCard>
            </div>

            <SubHeading>Advanced (EGO-50 – EGO-53)</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🛡️" title="EGO-50 — MEV Protection" accent="#ec4899">
                Commit-reveal ordering prevents front-running and sandwich attacks. Encrypted mempool decrypted only at block proposal time. Validators provably reordering TXs for profit are slashed 10% of stake.
              </InfoCard>
              <InfoCard icon="⛽" title="EGO-51 — Fee Market" accent="#ec4899">
                EIP-1559-style. Base fee adjusts ±12.5% per block based on 50% target utilisation. 100% of base fee burned (deflationary). <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>priority_fee_uegoc</code> tip paid to proposer. ✅ Implemented.
              </InfoCard>
              <InfoCard icon="🗳️" title="EGO-52 — On-Chain Governance" accent="#ec4899">
                Staked EGOC votes (1 staked EGOC = 1 vote). 10,000 EGOC to submit proposal. 7-day vote. 4% quorum. &gt;50% majority. 48h timelock before execution. 33% veto within 24h.
              </InfoCard>
              <InfoCard icon="🌐" title="EGO-53 — Decentralized Identity (DID)" accent="#ec4899">
                <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>did:ego:{'{'}egot_address{'}'}</code> format. Ed25519 + Dilithium-2 verification methods. Verifiable credentials as EGO-23 SBTs. Sign-in with Ego (SIWE-compatible challenge-response).
              </InfoCard>
            </div>

            <CalloutBox type="info">
              All EIPs are tracked as open GitHub issues (#11–#33) at <strong style={{ color: "#c9d4e3" }}>ego-blockchain/ego-blockchain-</strong>. Anyone can comment, propose changes, or open a new issue to submit an EIP. The DAO votes on acceptance — no gatekeeping by the founding team.
            </CalloutBox>
          </section>

          <Divider />

          {/* WALLETCONNECT */}
          <section id="walletconnect" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="EGO-25" title="WalletConnect — Connecting Wallets to dApps"
              color="#0066ff"
              subtitle="Scan a QR code, approve a connection, sign transactions from your phone or desktop — without giving the dApp your private key. EGO-25 is Ego's native wallet connection protocol." />
            <Prose>
              EGO-25 is inspired by WalletConnect v2 but uses Ego-native cryptography throughout: Ed25519 for connection signing, ML-KEM (Kyber-768) for session key encapsulation, and AES-256-GCM for all session traffic. No X25519, no ChaCha20 — quantum-safe from the first handshake.
            </Prose>

            <SubHeading>How It Works — Step by Step</SubHeading>
            <div style={{ marginBottom: 28 }}>
              <StepCard number="1" title="dApp generates a pairing URI">
                The dApp creates a random 32-byte topic and displays it as a QR code:<br />
                <code style={{ color: "#00e5b0", fontFamily: "monospace", fontSize: 12 }}>
                  egowc://relay.egoblockchain.com?topic=&lt;hex&gt;&amp;sym_key=&lt;hex&gt;&amp;version=2
                </code><br />
                The <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>sym_key</code> is a 32-byte pre-shared AES key for the pairing phase only.
              </StepCard>
              <StepCard number="2" title="Wallet scans the QR code">
                The Ego Desktop app or mobile wallet scans the URI. It connects to the relay server over WebSocket and subscribes to the pairing topic. Both sides now share the pairing channel.
              </StepCard>
              <StepCard number="3" title="Session proposal — Kyber key exchange">
                The dApp sends a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>SessionPropose</code> message containing its ML-KEM (Kyber-768) public key. The wallet encapsulates a fresh 32-byte session key using Kyber and sends back the ciphertext. Only the dApp can decapsulate it — establishing a session key neither side transmitted in plaintext.
              </StepCard>
              <StepCard number="4" title="Wallet approves the session">
                The wallet UI displays the dApp name, icon, and requested permissions (e.g. <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_sendTransaction</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_signMessage</code>). The user taps Approve. The wallet sends a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>SessionSettle</code> message — the session is active.
              </StepCard>
              <StepCard number="5" title="dApp sends signing requests">
                All subsequent messages are encrypted with AES-256-GCM using the session key. The dApp sends a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>SessionRequest</code> for each operation. The wallet decrypts, displays a structured prompt to the user, and signs only on approval.
              </StepCard>
              <StepCard number="6" title="User approves each request individually">
                The wallet shows the full transaction details — recipient, amount, contract, calldata — before signing. The user approves or rejects. The signed result is sent back to the dApp encrypted over the session channel.
              </StepCard>
              <StepCard number="7" title="Session expires or is disconnected">
                Sessions have a configurable TTL (default 7 days). Either party can disconnect at any time by sending a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>SessionDelete</code> message. The relay server forwards it and both sides discard their session keys.
              </StepCard>
            </div>

            <SubHeading>Session Encryption</SubHeading>
            <FormulaBlock>{`// Step 1 — dApp generates pairing pre-shared key
pairing_sym_key = random_bytes(32)
topic            = blake2s(pairing_sym_key)[0..16]
qr_uri = "egowc://relay.egoblockchain.com?topic=" + hex(topic)
       + "&sym_key=" + hex(pairing_sym_key) + "&version=2"

// Step 2 — Session key exchange (Kyber-768)
dapp_keypair = kyber768_keygen()
// dApp sends: kyber_public_key (encrypted with pairing_sym_key)
session_key, ciphertext = kyber768_encapsulate(dapp_keypair.public_key)
// Wallet sends back ciphertext; dApp decapsulates:
session_key = kyber768_decapsulate(ciphertext, dapp_keypair.secret_key)

// Step 3 — All session messages
nonce      = random_bytes(12)
ciphertext = aes256_gcm_encrypt(session_key, nonce, plaintext_json)
wire       = nonce || ciphertext        // 12 + N bytes`}</FormulaBlock>

            <SubHeading>Supported RPC Methods</SubHeading>
            <Table
              headers={["Method", "Description", "User Prompt"]}
              rows={[
                ["ego_sendTransaction", "Sign and submit a transaction to the network", "Shows recipient, amount, contract"],
                ["ego_signMessage", "Sign an arbitrary message (off-chain proof of identity)", "Shows raw message text"],
                ["ego_signTypedData", "Sign structured data per EGO-17 (permit, meta-tx)", "Shows parsed field names and values"],
                ["ego_getAccounts", "Return the connected wallet address", "Auto-approved after session established"],
                ["ego_chainId", "Return the active chain ID (e.g. 1 = Ego testnet)", "Auto-approved"],
                ["ego_switchChain", "Ask wallet to switch to a different Ego chain ID", "Shows chain name and ID"],
              ]}
            />

            <SubHeading>Relay Server Architecture</SubHeading>
            <Prose>
              The relay server is a stateless WebSocket message broker — it forwards encrypted envelopes between wallet and dApp but cannot read them (only the two parties hold the session key). The relay is horizontally scalable and can be self-hosted. Ego runs a public relay at <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>relay.egoblockchain.com</code>.
            </Prose>
            <FormulaBlock>{`// Message envelope (relay sees only this)
Envelope {
  topic:   bytes[16]   // routing key, non-secret
  payload: bytes       // AES-256-GCM encrypted JSON
  ttl:     u32         // seconds before relay discards
  tag:     u32         // 0=pairing, 1=session
}

// The relay cannot decrypt payload — it only routes by topic
// Both wallet and dApp connect to the relay via WSS
// relay.egoblockchain.com:443 (TLS + WebSocket)`}</FormulaBlock>

            <SubHeading>Using WalletConnect in Your dApp</SubHeading>
            <FormulaBlock>{`import { EgoWalletConnect } from "@ego-blockchain/sdk";

const wc = new EgoWalletConnect({
  projectId:   "your-project-id",
  relayUrl:    "wss://relay.egoblockchain.com",
  metadata: {
    name:        "My dApp",
    description: "Built on Ego Blockchain",
    url:         "https://mydapp.egoblockchain.com",
    icons:       ["https://mydapp.egoblockchain.com/icon.png"],
  },
});

// Generate QR code
const { uri, approval } = await wc.connect({
  requiredNamespaces: {
    ego: {
      methods:  ["ego_sendTransaction", "ego_signMessage"],
      chains:   ["ego:1"],     // chain ID 1 = testnet
      events:   ["accountsChanged"],
    },
  },
});
displayQRCode(uri);            // show QR in your UI

// Wait for wallet to scan and approve
const session = await approval();
const address = session.namespaces.ego.accounts[0];
console.log("Connected:", address);

// Send a transaction
const txHash = await wc.request({
  topic:   session.topic,
  chainId: "ego:1",
  request: {
    method: "ego_sendTransaction",
    params: [{
      from:    address,
      to:      "0xRecipientAddress...",
      value:   "1000000",          // 1 EGOC in uEGOC
      data:    "0x",
    }],
  },
});
console.log("TX hash:", txHash);`}</FormulaBlock>

            <SubHeading>Ego Desktop App — WalletConnect Support</SubHeading>
            <div style={{ marginBottom: 20 }} className="docs-grid-2">
              <InfoCard icon="📷" title="Scan from Desktop">
                The Ego Desktop app includes a built-in QR scanner. Open the app → WalletConnect tab → scan the dApp's QR code. The session is established within seconds using Kyber key exchange.
              </InfoCard>
              <InfoCard icon="🔔" title="Per-Request Approval">
                Every <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_sendTransaction</code> or <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_signMessage</code> call shows a native OS notification in the Desktop app. You approve or reject from the notification — your private key never leaves the app.
              </InfoCard>
              <InfoCard icon="⏱️" title="Session Management">
                Active sessions are listed in the Desktop app with the dApp name, icon, and expiry. Disconnect any session instantly. Sessions auto-expire after 7 days of inactivity.
              </InfoCard>
              <InfoCard icon="🔐" title="Key Safety">
                The Ego Desktop app never exposes private keys to the dApp or the relay. The relay only sees encrypted envelopes routed by topic. The dApp sees only the wallet address and signed transaction hashes.
              </InfoCard>
            </div>

            <CalloutBox type="success">
              The <code style={{ color: "#00e5b0" }}>sdk/walletconnect/</code> directory in the ego-blockchain repository contains the TypeScript WalletConnect client. Install with <code style={{ color: "#00e5b0" }}>npm install @ego-blockchain/sdk</code>.
            </CalloutBox>
          </section>

          <Divider />

          {/* EVM COMPATIBILITY */}
          <section id="evm" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="EGO-12" title="EVM Compatibility Layer" color="#f59e0b"
              subtitle="A full Ethereum Virtual Machine embedded inside every ego-node. Deploy Solidity contracts unchanged. MetaMask, Hardhat, Foundry, and Ethers.js all work out of the box." />
            <Prose>
              Ego embeds the battle-tested <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>revm</code> crate (Rust EVM) running the London spec at chain_id 1399. Every EVM opcode, precompile, and Solidity ABI encoding works identically to Ethereum. Urego (WASM) contracts and EVM (Solidity) contracts coexist on the same chain and can call each other via an ABI bridge.
            </Prose>

            <SubHeading>What's Supported</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🔢" title="Arithmetic & Bitwise" accent="#f59e0b">ADD, MUL, SUB, DIV, MOD, EXP, SHL, SHR (EIP-145) — all tested and passing with revm London spec.</InfoCard>
              <InfoCard icon="🗄️" title="Storage" accent="#f59e0b">SSTORE / SLOAD per-contract key-value storage, persistent across calls and blocks.</InfoCard>
              <InfoCard icon="📣" title="Events" accent="#f59e0b">LOG0 and LOG1 (up to LOG4) emit ABI-encoded events queryable by the EGO-14 indexer on port 8546.</InfoCard>
              <InfoCard icon="🔄" title="Contract Lifecycle" accent="#f59e0b">CREATE2 deterministic deployment, STATICCALL (including identity precompile 0x4), REVERT with return data, ETH value transfers.</InfoCard>
              <InfoCard icon="⛽" title="Gas & Resource Units" accent="#f59e0b">Gas consumption is tracked identically to Ethereum. Resource Units (RU) = gas × 10 for EGO-5 feeless metering.</InfoCard>
              <InfoCard icon="🦊" title="MetaMask Compatible" accent="#f59e0b">Add Ego network to MetaMask: RPC <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>http://localhost:8545</code>, chain ID 1399, symbol EGOC. Deploy from Remix in seconds.</InfoCard>
            </div>

            <SubHeading>EVM Test Suite (16 Tests — All Passing)</SubHeading>
            <FormulaBlock>{`cargo test -p ego-evm                    # run all 16 integration tests

test test_add                    ... ok   // ADD opcode
test test_mul                    ... ok   // MUL opcode
test test_sub                    ... ok   // SUB opcode
test test_exp                    ... ok   // EXP opcode
test test_storage_sstore_sload   ... ok   // SSTORE + SLOAD
test test_multiple_storage_slots ... ok   // multi-slot persistence
test test_log0_event             ... ok   // LOG0 event emission
test test_log1_event             ... ok   // LOG1 with topic
test test_revert_returns_data    ... ok   // REVERT + return data
test test_shl_opcode             ... ok   // SHL (EIP-145)
test test_shr_opcode             ... ok   // SHR (EIP-145)
test test_create2_deploys_child  ... ok   // CREATE2 deterministic deploy
test test_staticcall_identity_precompile ... ok  // precompile 0x4
test test_eth_value_transfer     ... ok   // ETH value between contracts
test test_gas_is_consumed        ... ok   // gas accounting
test test_ru_equals_gas_times_ten ... ok  // RU = gas × 10

test result: ok. 16 passed; 0 failed`}</FormulaBlock>

            <SubHeading>Connecting MetaMask to Ego Testnet</SubHeading>
            <FormulaBlock>{`Network Name:   Ego Testnet
RPC URL:        http://localhost:8545    (or your validator IP:8545)
Chain ID:       1399
Currency:       EGOC
Block Explorer: http://localhost:80     (nginx load-balancer)`}</FormulaBlock>

            <CalloutBox type="info">
              The EVM and Urego runtimes share the same block state. A Solidity contract can read storage set by a Urego contract and vice versa — via the Ego ABI bridge defined in EGO-12.
            </CalloutBox>
          </section>

          <Divider />

          {/* CROSS-CHAIN BRIDGE */}
          <section id="bridge" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="EGO-10" title="Cross-Chain Bridge" color="#0066ff"
              subtitle="Lock ERC-20 tokens or native ETH on Ethereum (BNB, Polygon) and receive wrapped tokens on Ego. Bridge back by burning on Ego and releasing on Ethereum." />
            <Prose>
              The bridge is implemented as <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>EgoLock.sol</code> deployed on Ethereum/BNB/Polygon. A relayer watches for <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Locked</code> events and calls <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>verify_and_mint</code> on the Ego chain. Phase 2 replaces the trusted relayer with a Groth16 ZK proof of the lock event — no trusted intermediary at all.
            </Prose>

            <SubHeading>Bridge In (ETH → Ego)</SubHeading>
            <div style={{ marginBottom: 20 }}>
              <StepCard number="1" title="User calls lock() or lockETH() on EgoLock.sol">
                Tokens are transferred to the EgoLock contract. A monotonic <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>lockNonce</code> is incremented. A <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Locked(sender, amount, token, nonce, ego_dest)</code> event is emitted.
              </StepCard>
              <StepCard number="2" title="Relayer observes the Locked event">
                The off-chain relayer watches Ethereum logs. On seeing <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>Locked</code>, it submits a <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>verify_and_mint</code> transaction to the Ego chain — minting wrapped tokens at the <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_dest</code> address.
              </StepCard>
              <StepCard number="3" title="Wrapped tokens credited on Ego">
                The user receives wETH / wUSDC / EGUSD on Ego. EGUSD (EGO-11) is the bridge-backed stablecoin: 1 EGUSD = 1 USDC locked in EgoLock.
              </StepCard>
            </div>

            <SubHeading>Bridge Out (Ego → ETH)</SubHeading>
            <div style={{ marginBottom: 20 }}>
              <StepCard number="1" title="User burns wrapped tokens on Ego">A <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>BridgeOut</code> event is emitted with a burn nonce.</StepCard>
              <StepCard number="2" title="Relayer calls unlock() on EgoLock.sol">The <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>burn_nonce</code> is checked against <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>usedBurnNonces</code> — replay protection. Tokens are released to the recipient on Ethereum.</StepCard>
            </div>

            <SubHeading>Security</SubHeading>
            <Table
              headers={["Control", "Mechanism"]}
              rows={[
                ["Replay protection", "usedBurnNonces mapping — each burn_nonce can only unlock once"],
                ["Phase 1 auth", "onlyRelayer modifier — trusted relayer address set by owner"],
                ["Phase 2 auth", "Groth16 ZK proof of Ego burn event — no trusted intermediary"],
                ["Rug protection", "Relayer cannot steal — can only unlock for valid Ego burns"],
                ["Pause switch", "Owner can pause all lock/unlock in emergency"],
                ["Token whitelist", "Only owner-whitelisted tokens can be locked"],
                ["Fee cap", "feeBps ≤ 100 (max 1%) enforced on-chain"],
              ]}
            />

            <FormulaBlock>{`// contracts/bridge/EgoLock.sol — key interface
function lock(address token, uint256 amount, string calldata ego_dest) external;
function lockETH(string calldata ego_dest) external payable;
function unlock(address token, uint256 amount, address recipient, uint64 burn_nonce) external onlyRelayer;
function unlockETH(address payable recipient, uint256 amount, uint64 burn_nonce) external onlyRelayer;

// Events watched by the relayer
event Locked(address indexed sender, uint256 amount, address indexed token, uint64 nonce, string ego_dest);
event Unlocked(address indexed recipient, uint256 amount, address indexed token, uint64 burn_nonce);`}</FormulaBlock>
          </section>

          <Divider />

          {/* BROWSER EXTENSION */}
          <section id="extension" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Wallet" title="Browser Extension Wallet" color="#8b5cf6"
              subtitle="A Chrome MV3 extension that injects window.ego and window.ethereum into every page — making any Ethereum dApp instantly compatible with the Ego network." />
            <Prose>
              The Ego Browser Extension ships as a Chrome Manifest V3 extension. It injects a provider into every web page, bridging dApp JSON-RPC calls to the extension's background service worker, which holds the encrypted keystore and signs with Dilithium-2 + Ed25519.
            </Prose>

            <SubHeading>Injected APIs</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🟣" title="window.ego (native)" accent="#8b5cf6">
                Ego-native provider. Supports <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_sendTransaction</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_signMessage</code>, <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>ego_signTypedData</code> with Dilithium-2 signing.
              </InfoCard>
              <InfoCard icon="🦊" title="window.ethereum (compat)" accent="#8b5cf6">
                EIP-1193 compatible shim. Existing Ethereum dApps (Uniswap, OpenSea interfaces, etc.) connect without modification. chain_id 1399.
              </InfoCard>
            </div>

            <FormulaBlock>{`// dApp connects to Ego extension (same as MetaMask)
const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
const txHash   = await window.ethereum.request({
  method: "eth_sendTransaction",
  params: [{ from: accounts[0], to: "0x...", value: "0xDE0B6B3A7640000" }],
});

// Or use the native Ego provider for full features
const address = await window.ego.request({ method: "ego_getAccounts" });`}</FormulaBlock>

            <SubHeading>Architecture</SubHeading>
            <Table
              headers={["Component", "Role"]}
              rows={[
                ["content.js", "Injected into every page — creates window.ego and window.ethereum proxies"],
                ["background.js (MV3 service worker)", "Holds encrypted keystore, handles signing requests, communicates with ego-node RPC"],
                ["popup.html / React", "Extension UI — account overview, transaction history, network switcher"],
                ["keystore.json", "AES-256-GCM encrypted key storage in chrome.storage.local"],
              ]}
            />

            <CalloutBox type="info">
              The extension is in the <code style={{ color: "#00e5b0" }}>sdk/walletconnect/</code> directory and built alongside the TypeScript SDK. Install from source: <code style={{ color: "#00e5b0" }}>npm run build:extension</code> → load unpacked in Chrome.
            </CalloutBox>
          </section>

          <Divider />

          {/* DEX / AMM */}
          <section id="dex" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="EGO-4" title="DEX & AMM" color="#f59e0b"
              subtitle="Constant-product automated market maker built on the EGO-4 standard. Swap EGOC, EGUSD, WBTC, and WETH in one on-chain transaction." />
            <Prose>
              The Ego DEX uses a Uniswap v2-style x×y=k invariant. Liquidity providers deposit token pairs, receive LP tokens proportional to their share of the pool, and earn 0.3% of every swap. Any EGO-20 pair can have a pool.
            </Prose>

            <SubHeading>Core Formula</SubHeading>
            <FormulaBlock>{`// Constant product invariant
x × y = k      // must hold after every swap

// Amount out for amount_in (0.3% fee)
amount_in_with_fee = amount_in × 997
amount_out = (amount_in_with_fee × reserve_out)
           / (reserve_in × 1000 + amount_in_with_fee)

// Price impact = 1 - (reserve_in / (reserve_in + amount_in))`}</FormulaBlock>

            <SubHeading>Supported Pools (Testnet)</SubHeading>
            <Table
              headers={["Pair", "Description"]}
              rows={[
                ["EGOC / EGUSD", "Primary liquidity pair — EGOC priced in USD stablecoin"],
                ["EGOC / WBTC", "Bitcoin exposure via wrapped BTC (EGO-10 bridge)"],
                ["EGOC / WETH", "Ethereum exposure via wrapped ETH (EGO-10 bridge)"],
                ["EGUSD / WBTC", "Stablecoin ↔ BTC for DeFi strategies"],
              ]}
            />

            <SubHeading>EGO-4 Interface</SubHeading>
            <FormulaBlock>{`// Urego contract interface (EGO-4 standard)
fn add_liquidity(token_a: Address, token_b: Address, amount_a: u64, amount_b: u64) -> u64
fn remove_liquidity(pair: Address, lp_amount: u64) -> (u64, u64)
fn swap_exact_in(token_in: Address, token_out: Address, amount_in: u64, min_out: u64) -> u64
fn swap_exact_out(token_in: Address, token_out: Address, amount_out: u64, max_in: u64) -> u64
fn get_price(token_a: Address, token_b: Address) -> u64    // price × 1e6`}</FormulaBlock>

            <CalloutBox type="success">
              The NFT Marketplace page in the Ego Desktop app uses the DEX for EGOC/EGUSD price display. Any wallet holding EGUSD (bridged from USDC) can trade directly without leaving the app.
            </CalloutBox>
          </section>

          <Divider />

          {/* P2P TRADE */}
          <section id="p2p" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Peer to Peer" title="P2P Trade & Multi-Chain Escrow" color="#22c55e"
              subtitle="A LocalCoinSwap-style marketplace inside Ego Desktop. People trade coins on seven networks for fiat paid off-chain, while the coins wait in an escrow that only the trade's rules can move." />
            <Prose>
              Offers, trades, disputes and reputation live on the Ego chain as market operations. The coins themselves are escrowed where they belong: EGOC on the Ego chain, every other coin on its own network. The buyer's app reads that network directly and only tells the buyer to pay once the escrow matches the trade.
            </Prose>

            <SubHeading>Coins and networks (testnet)</SubHeading>
            <Table
              headers={["Network", "Coins", "Escrow", "Test network"]}
              rows={[
                ["Ego", "EGOC", "Ego chain consensus, no contract", "Ego testnet"],
                ["Ethereum", "USDT, USDC, ETH", "EgoEscrow contract (Solidity 0.8.26)", "Sepolia"],
                ["BNB Chain", "USDT, USDC, BNB", "EgoEscrow contract", "BNB Chain testnet"],
                ["Polygon", "USDT, USDC, POL", "EgoEscrow contract", "Amoy"],
                ["Tron", "USDT (TRC-20), TRX", "EgoEscrow contract (TVM)", "Nile"],
                ["Solana", "USDT, USDC (SPL), SOL", "Ego escrow program, one account per trade", "Devnet"],
                ["Cardano", "ADA", "Aiken validator, Plutus V3, no deployment needed", "Preprod"],
              ]}
            />

            <SubHeading>Trade lifecycle</SubHeading>
            <StepCard number="1" title="Offer">A maker posts a buy or sell offer: coin, fiat currency, fixed price or a margin on the market price, limits, payment methods and a payment window of 15 minutes to 24 hours. A buy offer for an outside coin names the address that receives it.</StepCard>
            <StepCard number="2" title="Open">A taker opens a trade for an amount within the limits. The chain assigns an arbiter and, for outside coins, fixes the arbiter's address on that network.</StepCard>
            <StepCard number="3" title="Lock">Within 30 minutes the seller funds the escrow. For EGOC the coins move into the Ego escrow; for outside coins the seller funds the escrow on that network and records the contract, funder and transaction on Ego. The buyer's app verifies the escrow on the other chain before showing the payment instructions.</StepCard>
            <StepCard number="4" title="Pay">The buyer pays off-chain with the trade's payment reference and marks the trade paid, stating the name on the paying account in the signed, encrypted trade chat.</StepCard>
            <StepCard number="5" title="Settle">The seller checks their own account and releases, the buyer cancels, or either side opens a dispute and the arbiter rules. The outcome is recorded on Ego and executed by the escrow on its own network.</StepCard>

            <SubHeading>Who can move the escrow</SubHeading>
            <Table
              headers={["Action", "Who", "Result"]}
              rows={[
                ["Release", "Seller", "Buyer receives the amount; the 1% fee goes to the fee address (burned for EGOC)"],
                ["Cancel", "Buyer (signed, relayed by the seller's app)", "Seller gets everything back; the buyer pays no network fee"],
                ["Resolve", "Arbiter, only in a dispute", "To the buyer or back to the seller, never anywhere else"],
                ["Freeze", "Buyer or arbiter", "Stops the 30-day fallback while a case is open"],
                ["Reclaim", "Seller, after 30 days", "Seller gets the coins back unless the escrow is frozen"],
              ]}
            />

            <SubHeading>Escrow on each network</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🟢" title="Ego chain (EGOC)" accent="#22c55e">The coins sit at a reserved escrow address that no key controls. Settlements carry the seller's, buyer's or arbiter's Ed25519 signature in their body, and every validator checks the role, the trade state and the payout before applying them.</InfoCard>
              <InfoCard icon="🔷" title="EVM and Tron" accent="#22c55e">One EgoEscrow contract per network, keyed by keccak(tradeId, seller). No owner, no upgrade path, a reentrancy guard and balance-delta checks for unusual tokens. The buyer's cancellation is an EIP-712 signature that anyone can relay.</InfoCard>
              <InfoCard icon="🟣" title="Solana" accent="#22c55e">A native program; each trade gets its own escrow account at a program-derived address of (trade, seller), with a token vault for SPL coins. Payouts must reach the parties' own token accounts, and accounts close on settlement so the seller gets the rent back. The buyer's cancellation is checked with Solana's Ed25519 instruction.</InfoCard>
              <InfoCard icon="🔵" title="Cardano" accent="#22c55e">An Aiken validator whose address is its script hash, so it needs no deployment. The trade terms sit in an inline datum, a spend may only touch one escrow at a time, and the buyer's cancellation carries their key and signature in the redeemer.</InfoCard>
            </div>

            <SubHeading>Market operations on the Ego chain</SubHeading>
            <FormulaBlock>{`// Signed v2 transactions to the market escrow address.
// The memo commits to the body: market:<op>:<blake2s(body)>
market_offer        { side, asset, fiat, price, min, max, methods, window, payout_address? }
market_offer_close  { offer_id }
market_trade_open   { offer_id, amount, price, fiat, method, payout_address? }
market_lock         { trade_id, escrow?: { contract, funder, tx } }
market_paid         { trade_id }
market_dispute      { trade_id, reason }
market_feedback     { trade_id, rating, comment }
market_arbiter      { evm?, tron?, sol?, ada? }      // arbiters publish payout addresses

// Settlements carry their authority inside the body
market_settle       { trade_id, outcome, by, pubkey, signature, native_sig? }
// native_sig: the buyer's signed cancellation on the escrow's own chain.
// For Solana and Cardano every validator verifies it before accepting the refund.`}</FormulaBlock>

            <SubHeading>Payment safety</SubHeading>
            <div style={{ marginBottom: 24 }} className="docs-grid-2">
              <InfoCard icon="🔖" title="Payment reference" accent="#22c55e">Every trade has a short reference such as <code style={{ color: "#00e5b0", fontFamily: "monospace" }}>EGO-K7F3Q2MZ</code>, derived from the trade ID. The buyer puts it in the payment description; the seller searches for it.</InfoCard>
              <InfoCard icon="🪪" title="Payer name" accent="#22c55e">When marking a trade paid, the buyer states the name on the paying account. It travels only in the signed, encrypted trade chat. Payments from anyone else are returned, not released.</InfoCard>
              <InfoCard icon="✅" title="Release checklist" accent="#22c55e">Release stays disabled until the seller confirms, in their own banking app, the amount, the sender's name and the reference.</InfoCard>
              <InfoCard icon="⚖️" title="Evidence rules" accent="#22c55e">Arbiters do not rule on screenshots. They ask for the bank's transfer ID, the original payment email with its DKIM signature, or a live screen share of the banking app.</InfoCard>
            </div>

            <SubHeading>Fees and limits</SubHeading>
            <Table
              headers={["Rule", "Value"]}
              rows={[
                ["Maker fee", "1%, charged only when a trade completes (burned for EGOC)"],
                ["Small ADA trades", "No fee under 100 ADA, since Cardano cannot pay out less than 1 ADA"],
                ["Minimum trade", "1 EGOC, 0.01 SOL, 10 ADA"],
                ["Seller funding window", "30 minutes after the trade opens"],
                ["Buyer payment window", "15 minutes to 24 hours, set by the offer"],
                ["Buyer dispute delay", "1 hour after marking the trade paid"],
                ["Escrow fallback", "Seller may reclaim after 30 days unless frozen"],
                ["Open offers per maker", "20; offers expire after 30 days"],
                ["Trades waiting for a seller, per taker", "5"],
              ]}
            />

            <CalloutBox type="warning">
              P2P Trade is on testnet. The escrows pass their own test suites (24 EVM tests, 15 Solana tests run against the compiled program, 18 Cardano validator tests, and checks of real transactions against the validator), but they have not been audited yet. An independent security audit, and a Solana program locked against upgrades, come before mainnet.
            </CalloutBox>
          </section>

          <Divider />

          {/* MOBILE WALLET */}
          <section id="mobile" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Mobile" title="Mobile Wallet" color="#00e5b0"
              subtitle="React Native + Expo wallet for iOS and Android. Full feature parity with the desktop app — send, receive, encrypted storage, P2P chat, and WalletConnect QR scanning." />
            <Prose>
              The Ego Mobile Wallet is built with React Native (Expo) and shares the TypeScript SDK with the web dApp ecosystem. All cryptographic operations mirror the desktop: Ed25519 + Dilithium-2 for signing, ML-KEM for Messenger key exchange, AES-256-GCM for file encryption. Keys are stored in the device secure enclave via Expo SecureStore.
            </Prose>

            <SubHeading>Feature Parity with Desktop</SubHeading>
            <Table
              headers={["Feature", "Mobile", "Desktop"]}
              rows={[
                ["Send / Receive EGOC", "✅", "✅"],
                ["QR code address display", "✅", "✅"],
                ["WalletConnect dApp connection", "✅ (camera scan)", "✅ (built-in scanner)"],
                ["AES-256-GCM file encryption", "✅", "✅"],
                ["P2P Messenger (Kyber E2E)", "✅", "✅"],
                ["24-word recovery phrase", "✅", "✅"],
                ["Dilithium-2 signing", "✅", "✅"],
                ["Hardware key storage", "✅ Expo SecureStore", "✅ OS keychain"],
                ["Deploy Urego contracts", "⏳ Planned", "✅"],
              ]}
            />

            <SubHeading>Tech Stack</SubHeading>
            <FormulaBlock>{`React Native (Expo SDK 52)
  ├── @ego-blockchain/sdk      — HTTP RPC client, tx signing
  ├── expo-secure-store        — key storage in device secure enclave
  ├── expo-camera              — QR scanner for WalletConnect
  ├── @noble/ed25519           — Ed25519 signing
  └── aes-js                   — AES-256-GCM encryption

// Build
npx expo build:android   # APK / AAB
npx expo build:ios       # IPA (requires Apple Developer account)`}</FormulaBlock>

            <CalloutBox type="warning">
              The mobile wallet is in active development. TestFlight (iOS) and Play Store open beta are planned after testnet stabilises.
            </CalloutBox>
          </section>

          <Divider />

          {/* PRE-SALE */}
          <section id="presale" style={{ marginBottom: 80, scrollMarginTop: 110 }}>
            <SectionHeader eyebrow="Seed Round" title="Pre-Sale — EGOC Seed Round" color="#8b5cf6"
              subtitle="The Ego Blockchain Seed Round is currently live. Buy EGOC at $0.008 — before mainnet launch at $0.02." />

            <Prose>
              The pre-sale allows early supporters to acquire EGOC at a seed-round discount before the network launches on mainnet. All purchases generate an encrypted IOU file — your cryptographic proof of purchase — redeemable as a genesis-block airdrop when mainnet goes live.
            </Prose>

            <SubHeading>Seed Round Terms</SubHeading>
            <Table
              headers={["Parameter", "Value"]}
              rows={[
                ["Seed price", "$0.008 per EGOC — 40,000,000 EGOC cap"],
                ["Private price", "$0.012 per EGOC — 40,000,000 EGOC cap"],
                ["Launch price", "$0.02 per EGOC (60% discount at seed)"],
                ["Total raise", "$800,000 for 8% of supply — $20M fully diluted"],
                ["Allocation source", "200,000,000 EGOC ecosystem pool"],
                ["Minimum purchase", "$10 USD equivalent"],
                ["Payment methods", "BTC, ETH, USDT, USDC, BNB, ADA, SOL, TRX, or credit/debit card (Stripe)"],
                ["Proof of purchase", "Encrypted IOU file (AES-256-GCM, password-protected)"],
                ["Delivery", "Genesis block airdrop to your mainnet address"],
              ]}
            />

            <SubHeading>Crypto Payment Flow</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Choose coin and amount">Open Ego Desktop → Wallet → Pre-Sale. Select payment coin (BTC, ETH, USDT, USDC, BNB, ADA, SOL, TRX) and enter the amount.</StepCard>
              <StepCard number="2" title="Get deposit address + EGOC allocation">The app calculates your EGOC allocation at the live tier price, served by the pre-sale proxy, and shows the exact treasury deposit address for your chosen coin.</StepCard>
              <StepCard number="3" title="Send crypto manually">Transfer the exact amount to the deposit address from your external wallet. No middleman — funds go directly to the Ego team treasury.</StepCard>
              <StepCard number="4" title="Download your IOU file">Set a password and download the encrypted IOU JSON file. This is your proof of purchase — keep it safe. The IOU is tied to your mainnet address.</StepCard>
            </div>

            <SubHeading>Card Payment Flow (Stripe)</SubHeading>
            <div style={{ marginBottom: 24 }}>
              <StepCard number="1" title="Choose card payment">Select the card tab in the Pre-Sale modal and enter USD amount (minimum $10).</StepCard>
              <StepCard number="2" title="Stripe Checkout">A secure Stripe Checkout page opens. Pay with any credit or debit card. The Stripe secret key is kept server-side — never exposed in the app.</StepCard>
              <StepCard number="3" title="Payment verified">After payment, the app verifies the session with Stripe and issues your encrypted IOU file.</StepCard>
            </div>

            <SubHeading>IOU File Format</SubHeading>
            <FormulaBlock>{`{
  "version":     1,
  "id":          "<uuid>",
  "ego_presale": true,
  "network":     "ego-mainnet",
  "issued_at":   <unix_timestamp>,
  "round":       "Seed Round",
  "payment": {
    "coin":            "ETH",
    "deposit_address": "0xD4f2B1fA44668B806290A4c3CB758ABb7EF35C64",
    "amount":          0.25
  },
  "allocation": {
    "egoc_amount":        250.0,
    "usd_value":          500.00,
    "price_per_egoc_usd": 2.00
  },
  "genesis_note": "This allocation will be credited in the Ego Chain Genesis Block...",
  "crypto": {
    "cipher":     "aes-256-gcm",
    "kdf":        "blake3",
    "salt":       "<hex>",
    "nonce":      "<hex>",
    "ciphertext": "<hex>"   // AES-256-GCM encrypted allocation details
  }
}

// Key derivation: key = BLAKE3(password_bytes || salt)
// Plaintext (encrypted): mainnet_address, testnet_address, egoc_amount, usd_value, deposit_address, round`}</FormulaBlock>

            <SubHeading>Treasury Deposit Addresses</SubHeading>
            <Table
              headers={["Coin", "Network", "Deposit Address"]}
              rows={[
                ["BTC", "Bitcoin", "bc1qaqx0xf9sv0ktmtcxlzzh7t7kf59nwu8c0vlqhg"],
                ["ETH / USDT / USDC / BNB", "Ethereum / ERC-20 / BEP-20", "0xD4f2B1fA44668B806290A4c3CB758ABb7EF35C64"],
                ["SOL", "Solana", "9PZzHQYohiR9fTKTJXUaRYKv6doM4NQPJZKcrVvTJbbW"],
                ["TRX", "Tron", "TSZnnQGN8idN6vEU66NX1ek1AtwmHbYLRx"],
                ["ADA", "Cardano", "addr1qyp35j52jw8tmg85wvll3p5krsgkpttxa65kxav4mc56g73fmcra587acj9n8zsqm8u55zvumpff3mrkt9865jswu4gql452dd"],
              ]}
            />

            <CalloutBox type="warning">
              Always verify you are sending to the correct address before transferring. Crypto transactions are irreversible. Keep your IOU file and password in a safe place — they are your only proof of purchase until mainnet launch.
            </CalloutBox>
          </section>

          {/* CTA */}
          <div style={{
            border: "1px solid rgba(0,229,176,0.18)", borderRadius: 16, padding: "32px 36px",
            background: "linear-gradient(135deg, rgba(0,229,176,0.04), rgba(0,102,255,0.04))",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20,
          }}>
            <div>
              <div style={{ color: "#e0e8f4", fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Read the full technical whitepaper</div>
              <div style={{ color: "#5a6a80", fontSize: 14 }}>26 pages of formal specifications, cryptographic proofs, and protocol parameters.</div>
            </div>
            <a href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer" style={{
              background: "rgba(0,229,176,0.12)", border: "1px solid rgba(0,229,176,0.30)",
              borderRadius: 8, color: "#00e5b0", padding: "12px 24px",
              textDecoration: "none", fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap",
            }}>↓ Download PDF</a>
          </div>

        </main>
      </div>
      <MouseFooter />
    </div>
  );
}
